import { useCallback, useEffect, useRef, useState } from "react";
import { Loader2, Eye, CheckCircle2, XCircle, RefreshCcw, Plus, Pencil, Save, ChevronDown, ChevronUp, Trash2, AlertTriangle } from "lucide-react";
import { ReceiptViewer } from "../components/ReceiptViewer";
import { TransferAccountsCard } from "../components/TransferAccountsCard";
import { PhotoThumb } from "../components/ImageLightbox";
import { PaymentApprovalModal } from "../components/PaymentApprovalModal";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Checkbox } from "../components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, apiError, money, fileUrl } from "../lib/api";

const PAYMENT_STATUS = {
  uploaded: { label: "En revisión", cls: "bg-hz-warning/10 text-hz-warning" },
  approved: { label: "Aprobado", cls: "bg-hz-success/10 text-hz-success" },
  rejected: { label: "Rechazado", cls: "bg-hz-error/10 text-hz-error" },
  correction_requested: { label: "Corrección solicitada", cls: "bg-hz-warning/10 text-hz-warning" },
  pending: { label: "Pendiente", cls: "bg-hz-gray/30 text-hz-ink" },
};

const ROLE_LABELS = {
  admin: "Administrador",
  welcome_coordinator: "Coordinador de bienvenida",
  usher: "Ujier",
  room_manager: "Responsable de salón",
  viewer: "Solo lectura",
  child_care_staff: "Cuidado infantil",
  payment_reviewer: "Revisor de pagos",
};

const WS_REG_LABELS = { pending: "Inscripción pendiente", active: "Inscripción activa", cancelled: "Inscripción cancelada" };

const INCONSISTENCY_LABELS = {
  receipt_without_status: "Comprobante recibido sin cambio de estado (el público ya lo ve como 'en revisión')",
  status_without_receipt: "Estado 'en revisión' sin comprobante registrado",
  notification_without_receipt: "Correo de comprobante recibido sin archivo persistido",
  other: "Inconsistencia de pago",
};

const MemberBreakdownCard = ({ m, defaultOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-lg border border-hz-gray/40" data-testid={`pay-member-${m.folio}`}>
      <button type="button" onClick={() => setOpen((o) => !o)} data-testid={`pay-member-toggle-${m.folio}`} className="flex w-full items-center justify-between gap-2 px-3 py-2 text-left">
        <span className="text-xs font-bold text-hz-ink">{m.full_name} · <span className="font-extrabold tracking-wider text-hz-navy">{m.folio}</span></span>
        <span className="flex shrink-0 items-center gap-1.5 text-xs font-bold text-hz-navy">{money(m.subtotal)}{open ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}</span>
      </button>
      {open && (
        <div className="border-t border-hz-gray/30 px-3 py-2 text-xs">
          <div className="flex items-center justify-between gap-2 py-1">
            <span className="text-hz-ink">{m.general_event.name}: {m.general_event.selected ? "Seleccionado" : "No seleccionado"}</span>
            <span className="shrink-0 font-bold text-hz-navy">{m.general_event.selected ? money(m.general_event.amount) : "—"}</span>
          </div>
          <div className="flex items-center justify-between gap-2 py-1">
            <span className="text-hz-ink">{m.dinner.name}: {m.dinner.selected ? "Seleccionada" : "No seleccionada"}</span>
            <span className="shrink-0 font-bold text-hz-navy">{m.dinner.selected ? money(m.dinner.amount) : "—"}</span>
          </div>
          {m.dinner_reservation === "waitlisted" && (
            <p className="rounded-md border border-hz-warning/40 bg-hz-warning/10 px-2 py-1 font-semibold text-hz-ink" data-testid={`pay-member-dinner-waitlist-${m.folio}`}>
              Cena en lista de espera: no actives su QR de cena hasta que admin libere cupo.
            </p>
          )}
          {m.dinner_reservation === "reserved" && m.dinner.selected && (
            <p className="px-2 py-1 font-semibold text-hz-ink/60" data-testid={`pay-member-dinner-reserved-${m.folio}`}>
              Reserva de cena temporal vigente; al aprobar se confirma su lugar.
            </p>
          )}
          {m.workshops.map((w) => (
            <div key={w.id} className="border-t border-hz-gray/20 py-1.5" data-testid={`pay-member-ws-${m.folio}-${w.id}`}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-semibold text-hz-ink">Taller: {w.name}</span>
                <span className="shrink-0 font-bold text-hz-navy">{w.registration_status === "cancelled" ? "—" : money(w.price)}</span>
              </div>
              <p className="text-hz-ink/60">{w.schedule_text || "Horario por confirmar"}{w.duration_minutes ? ` · ${w.duration_minutes} min` : ""}{w.room_name ? ` · ${w.room_name}` : ""}</p>
              <p className={`font-semibold ${w.registration_status === "cancelled" ? "text-hz-error" : "text-hz-ink/60"}`}>
                {WS_REG_LABELS[w.registration_status] || w.registration_status}{w.registration_status === "cancelled" ? " (estaba incluido originalmente)" : ""}
              </p>
            </div>
          ))}
          <div className="flex items-center justify-between border-t border-hz-gray/30 py-1.5 font-bold text-hz-navy">
            <span>Subtotal</span><span data-testid={`pay-member-subtotal-${m.folio}`}>{money(m.subtotal)}</span>
          </div>
        </div>
      )}
    </div>
  );
};

const PaymentTotalsCompare = ({ expected, reported, childcare }) => {
  const total = Math.round((expected + (childcare?.subtotal || 0)) * 100) / 100;
  const diff = Math.round(((reported || 0) - total) * 100) / 100;
  return (
    <div className="rounded-lg border border-hz-gray/40" data-testid="payment-totals-compare">
      {childcare?.subtotal > 0 && (
        <div className="flex items-center justify-between gap-2 px-3 py-1.5 text-xs" data-testid="payment-childcare-line">
          <span className="text-hz-ink">Atención/cuidado infantil · {childcare.count} niño(s){childcare.unit_price != null ? ` · ${money(childcare.unit_price)} c/u` : ""}</span>
          <span className="shrink-0 font-bold text-hz-navy">{money(childcare.subtotal)}</span>
        </div>
      )}
      <div className="flex items-center justify-between px-3 py-1.5 text-xs">
        <span className="text-hz-ink">Total esperado</span>
        <span className="font-bold text-hz-navy" data-testid="payment-expected-total">{money(total)}</span>
      </div>
      <div className="flex items-center justify-between px-3 py-1.5 text-xs">
        <span className="text-hz-ink">Monto reportado</span>
        <span className="font-bold text-hz-navy" data-testid="payment-reported-amount">{money(reported)}</span>
      </div>
      {diff !== 0 && (
        <div className="flex items-center justify-between gap-2 border-t border-hz-warning/40 bg-hz-warning/10 px-3 py-1.5 text-xs font-bold text-hz-warning" data-testid="payment-diff-warning">
          <span>Diferencia ({diff > 0 ? "reportado de más" : "reportado de menos"}) — la decisión final es del revisor</span>
          <span className="shrink-0">{money(Math.abs(diff))}</span>
        </div>
      )}
    </div>
  );
};

export const GroupPaymentsQueue = ({ readOnly = false, isAdmin = false, selectedRefs, onToggleRef, onToggleMany, refreshKey = 0, onLoadedIds }) => {
  const [payments, setPayments] = useState([]);
  const [statusFilter, setStatusFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyReceipts, setHistoryReceipts] = useState([]);
  const [confirming, setConfirming] = useState(false);

  const [page, setPage] = useState(1);
  const PAGE_SIZE = 50;

  const load = useCallback((status = statusFilter, p = page) => {
    api.get("/admin/group-payments", { params: { page: p, page_size: PAGE_SIZE, ...(status ? { status } : {}) } }).then((r) => {
      const list = r.data.payments || [];
      setPayments(list);
      onLoadedIds?.(list.map((pt) => pt.id));
    }).catch(() => {});
  }, [statusFilter, page, refreshKey, onLoadedIds]);

  useEffect(() => { load(); }, [load]);

  const openHistory = async (paymentId) => {
    try {
      const { data } = await api.get(`/admin/group-payments/${paymentId}/history`);
      setHistory(data.history || []);
      setHistoryReceipts(data.receipts || []);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const review = async (action) => {
    if (acting) return;
    if ((action === "reject" || action === "correction") && notes.trim().length < 5) {
      toast.error("Describe el motivo en las notas (mínimo 5 caracteres).");
      return;
    }
    setActing(true);
    try {
      const { data: rv } = await api.post(`/admin/group-payments/${selected.id}/review`, { action, notes: notes.trim() || null });
      toast.success(action === "approve" ? "Pago de grupo aprobado. Accesos y QR activados para todos los adultos del grupo." : action === "reject" ? "Pago de grupo rechazado." : "Corrección solicitada.");
      if (action === "approve" && rv?.dinner_conflicts > 0) {
        toast.warning(`${rv.dinner_conflicts} cena(s) no se activaron (reserva vencida, liberada o en lista de espera). Revísalas en /admin → Cena.`, { duration: 8000 });
      }
      setSelected(null);
      setNotes("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setActing(false);
    }
  };

  if (payments.length === 0) return null;

  return (
    <div className="mt-8" data-testid="group-payments-section">
      <p className="text-sm font-bold text-hz-navy">Pagos de grupo (comprobante único por grupo)</p>
      <p className="mt-0.5 text-xs text-hz-ink/60" data-testid="group-payments-rule-note">
        La aprobación del comprobante activa los accesos incluidos del registro (en registros familiares/grupales, los de todos los adultos). No existe aprobación parcial por persona.
      </p>
      <div className="mt-2 flex flex-wrap gap-2">
        {[{ id: "", label: "Todos" }, { id: "uploaded", label: "En revisión" }, { id: "approved", label: "Aprobados" }, { id: "correction_requested", label: "Corrección solicitada" }, { id: "rejected", label: "Rechazados" }].map((f) => (
          <button key={f.id} onClick={() => setStatusFilter(f.id)} data-testid={`group-payments-filter-${f.id || "all"}`}
            className={`rounded-full px-3 py-1 text-xs font-bold transition-colors ${statusFilter === f.id ? "bg-hz-navy text-white" : "bg-white text-hz-navy border border-hz-gray/50"}`}>
            {f.label}
          </button>
        ))}
      </div>
      <div className="mt-3 overflow-x-auto rounded-xl border border-hz-gray/40 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              {isAdmin && (
                <TableHead className="w-10">
                  <Checkbox checked={payments.length > 0 && payments.every((p) => selectedRefs?.has(`group:${p.id}`))}
                    onCheckedChange={(c) => onToggleMany?.(payments.map((p) => `group:${p.id}`), !!c)}
                    aria-label="Seleccionar todos los pagos de grupo de esta página" data-testid="group-pay-select-page-checkbox" />
                </TableHead>
              )}
              <TableHead className="font-bold text-hz-navy">Folio</TableHead>
              <TableHead className="font-bold text-hz-navy">Grupo / Contacto</TableHead>
              <TableHead className="font-bold text-hz-navy">Monto</TableHead>
              <TableHead className="font-bold text-hz-navy">Estado</TableHead>
              <TableHead className="font-bold text-hz-navy">Fecha</TableHead>
              <TableHead className="font-bold text-hz-navy">Revisado por</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {payments.map((p) => (
              <TableRow key={p.id} onClick={() => { setSelected(p); setNotes(p.review_notes || ""); }} className={`cursor-pointer hover:bg-hz-bg ${selectedRefs?.has(`group:${p.id}`) ? "bg-hz-cyan/5" : ""}`} data-testid={`group-payment-row-${p.id}`}>
                {isAdmin && (
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox checked={selectedRefs?.has(`group:${p.id}`)} onCheckedChange={() => onToggleRef?.(`group:${p.id}`)} aria-label={`Seleccionar pago ${p.display_folio || p.group_folio}`} data-testid={`group-pay-select-${p.id}`} />
                  </TableCell>
                )}
                <TableCell className="font-bold text-hz-navy">{p.display_folio || p.group_folio}<br /><span className="text-[10px] font-semibold uppercase tracking-wide text-hz-ink/50" data-testid={`group-payment-type-${p.id}`}>{p.display_registration_type}</span></TableCell>
                <TableCell className="font-semibold">{p.group_name || "—"}<br /><span className="text-xs font-normal text-hz-ink/60">{p.primary_contact_name}</span></TableCell>
                <TableCell>{money(p.amount)}</TableCell>
                <TableCell>
                  <span className={`rounded-full px-3 py-1 text-xs font-bold ${PAYMENT_STATUS[p.status]?.cls || ""}`}>
                    {PAYMENT_STATUS[p.status]?.label || p.status}
                  </span>
                </TableCell>
                <TableCell className="text-xs">{new Date(p.created_at).toLocaleString("es-MX")}</TableCell>
                <TableCell className="text-xs">{p.reviewed_by_name || "—"}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setHistory(null); setHistoryReceipts([]); setConfirming(false); } }}>
        <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-1rem)] flex-col gap-0 overflow-hidden bg-white p-0 sm:max-w-2xl">
          <div className="flex-none border-b border-hz-gray/40 px-4 py-3 pr-12 sm:px-6">
            <DialogHeader>
              <DialogTitle className="font-bold text-hz-navy" data-testid="group-payment-detail-title">
                Revisar pago — {selected?.display_registration_type || selected?.registration_type_label || "Registro"}
              </DialogTitle>
              <p className="text-xs font-semibold text-hz-ink/70">
                Folio <span className="font-extrabold text-hz-navy" data-testid="group-payment-folio">{selected?.display_folio || selected?.group_folio}</span>
                {selected && <> · <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${PAYMENT_STATUS[selected.status]?.cls || ""}`}>{PAYMENT_STATUS[selected.status]?.label || selected.status}</span></>}
              </p>
            </DialogHeader>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6" data-testid="group-payment-dialog-body">
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-xs text-hz-ink/60">Grupo</p><p className="font-bold">{selected.group_name || "—"}</p></div>
                <div><p className="text-xs text-hz-ink/60">Contacto</p><p className="font-bold">{selected.primary_contact_name}</p></div>
                <div><p className="text-xs text-hz-ink/60">Monto (total)</p><p className="font-bold">{money(selected.amount)}</p></div>
                <div><p className="text-xs text-hz-ink/60">Concepto</p><p className="font-semibold">{selected.concept}</p></div>
              </div>
              {selected.member_folios?.length > 0 && (
                <div className="text-xs" data-testid="group-payment-member-folios">
                  <p className="font-semibold text-hz-ink/60">Folios individuales de los adultos</p>
                  <p className="mt-0.5 font-bold text-hz-navy">{selected.member_folios.join(" · ")}</p>
                </div>
              )}
              {selected.members_breakdown?.length > 0 && (
                <div className="space-y-2" data-testid="group-payment-breakdown">
                  <p className="text-xs font-bold text-hz-navy">Desglose por adulto (precios históricos)</p>
                  {selected.members_breakdown.map((m) => <MemberBreakdownCard key={m.attendee_id} m={m} />)}
                  <PaymentTotalsCompare expected={selected.members_breakdown.reduce((s, m) => s + m.subtotal, 0)} reported={selected.amount} childcare={selected.childcare} />
                </div>
              )}
              <Button variant="outline" size="sm" onClick={() => openHistory(selected.id)} data-testid="group-payment-history-btn" className="font-semibold">
                Ver historial del pago
              </Button>
              {history && (
                <div className="rounded-lg border border-hz-gray/40" data-testid="group-payment-history">
                  <p className="border-b border-hz-gray/40 px-3 py-2 text-xs font-bold text-hz-navy">Versiones del comprobante</p>
                  {history.map((v) => (
                    <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 text-xs" data-testid={`group-payment-history-v${v.version || 1}`}>
                      <span className="font-semibold">v{v.version || 1} · {PAYMENT_STATUS[v.status]?.label || v.status}</span>
                      <span className="text-hz-ink/60">{new Date(v.created_at).toLocaleString("es-MX")}{v.reviewed_by_name ? ` · ${v.reviewed_by_name}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
              <p className="rounded-lg border border-hz-cyan/30 bg-hz-cyan/5 px-3 py-2 text-xs font-medium text-hz-ink" data-testid="group-payment-approve-note">
                {selected?.registration_type === "individual"
                  ? "La aprobación de este comprobante activará los accesos incluidos para esta persona."
                  : "La aprobación de este comprobante activará los accesos incluidos para todos los adultos del grupo."}
              </p>
              {selected.attendee_photo_url && (
                <div className="flex items-center gap-3 rounded-xl bg-hz-bg p-3" data-testid="group-payment-attendee-photo-box">
                  <PhotoThumb src={selected.attendee_photo_url} alt={`Fotografía de ${selected.attendee_name || selected.primary_contact_name}`} className="h-14 w-14 rounded-xl border border-hz-gray/50 object-cover" testId="group-payment-attendee-photo" />
                  <div>
                    <p className="text-sm font-bold text-hz-ink">{selected.attendee_name || selected.primary_contact_name}</p>
                    <p className="text-xs text-hz-ink/60">{selected.display_registration_type}</p>
                  </div>
                </div>
              )}
              <ReceiptViewer receipt={selected.receipt} versions={historyReceipts} canDownload={!readOnly}
                title={`Comprobante de pago ${selected.display_folio || ""}`} testId="group-receipt-viewer" />
              <TransferAccountAssigner payment={selected} isGroup readOnly={readOnly}
                onSaved={(v) => setSelected((s) => s && ({ ...s, transfer_account_id: v }))} testId="group-payment-transfer-account" />
              {selected.review_notes && (
                <p className="rounded-lg bg-hz-bg px-3 py-2 text-xs font-medium text-hz-ink" data-testid="group-payment-review-notes">Notas: {selected.review_notes}</p>
              )}
            </div>
          )}
          </div>
          {selected && !readOnly && selected.status !== "approved" && (
            <div className="flex-none space-y-3 border-t border-hz-gray/40 bg-white px-4 py-3 sm:px-6" data-testid="group-payment-dialog-footer">
              <Label className="text-xs font-semibold text-hz-ink">Notas de revisión (obligatorias para rechazar o solicitar corrección)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="group-payment-notes-input" rows={2} />
              {confirming ? (
                <div className="rounded-lg border border-hz-warning/50 bg-hz-warning/5 p-3" data-testid="group-payment-confirm-approve">
                  <p className="text-xs font-bold text-hz-ink">¿Confirmar aprobación? Se activarán los accesos y QR de todos los adultos.</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Button onClick={() => review("approve")} disabled={acting} data-testid="group-payment-approve-btn" className="bg-hz-success font-bold text-white hover:bg-hz-success/90">
                      {acting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-2 h-4 w-4" />} Confirmar aprobación
                    </Button>
                    <Button variant="outline" onClick={() => setConfirming(false)} disabled={acting} data-testid="group-payment-confirm-cancel-btn" className="font-bold">Volver</Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Button onClick={() => setConfirming(true)} disabled={acting} data-testid="group-payment-approve-open-btn" className="bg-hz-success font-bold text-white hover:bg-hz-success/90">
                    <CheckCircle2 className="mr-1 h-4 w-4" /> Aprobar pago
                  </Button>
                  <Button onClick={() => review("correction")} disabled={acting} data-testid="group-payment-correction-btn" className="h-auto whitespace-normal bg-hz-warning px-2 text-center text-xs font-bold leading-tight text-white hover:bg-hz-warning/90 sm:text-sm">
                    <RefreshCcw className="mr-1 h-4 w-4" /> Solicitar corrección
                  </Button>
                  <Button onClick={() => review("reject")} disabled={acting} data-testid="group-payment-reject-btn" className="bg-hz-error font-bold text-white hover:bg-hz-error/90">
                    <XCircle className="mr-1 h-4 w-4" /> Rechazar
                  </Button>
                  <Button variant="outline" onClick={() => { setSelected(null); setHistory(null); setHistoryReceipts([]); }} disabled={acting} data-testid="group-payment-cancel-btn" className="font-bold">Cancelar</Button>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const PaymentsTab = ({ readOnly = false, role = null }) => {
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("uploaded");
  const [dateFilter, setDateFilter] = useState("");
  const [selected, setSelected] = useState(null);
  const [notes, setNotes] = useState("");
  const [acting, setActing] = useState(false);
  const [history, setHistory] = useState(null);
  const [historyReceipts, setHistoryReceipts] = useState([]);
  const [confirming, setConfirming] = useState(false);
  const isAdmin = role === "admin";
  const [selectedRefs, setSelectedRefs] = useState(new Set());
  const [groupIds, setGroupIds] = useState([]);
  const [preview, setPreview] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkConfirm, setBulkConfirm] = useState("");
  const [bulkAck, setBulkAck] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const bulkKeyRef = useRef(null);
  const [inconsistencies, setInconsistencies] = useState([]);
  const [syncingFolio, setSyncingFolio] = useState(null);
  // Fase 2B: modal de aprobación por acceso (pagos parciales / asignaciones)
  const [approvalPaymentId, setApprovalPaymentId] = useState(null);
  const canApproveByAccess = isAdmin || role === "payment_reviewer";

  const loadInconsistencies = useCallback(() => {
    api.get("/admin/diagnostics/payment-inconsistencies")
      .then((r) => setInconsistencies(r.data.items || []))
      .catch(() => {});
  }, []);

  useEffect(() => { loadInconsistencies(); }, [loadInconsistencies, refreshKey]);

  const syncReceiptState = async (folio) => {
    if (syncingFolio) return;
    setSyncingFolio(folio);
    try {
      const { data } = await api.post(`/admin/diagnostics/payment/${encodeURIComponent(folio)}/sync-receipt-state`);
      toast.success(data.message || "Estado sincronizado.");
      loadInconsistencies();
      load(filter, dateFilter);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : apiError(err));
    } finally {
      setSyncingFolio(null);
    }
  };

  const handleGroupLoaded = useCallback((ids) => setGroupIds(ids), []);

  const toggleRef = (ref) => {
    setSelectedRefs((prev) => {
      const next = new Set(prev);
      if (next.has(ref)) next.delete(ref); else next.add(ref);
      return next;
    });
  };

  const toggleMany = (refs, select) => {
    setSelectedRefs((prev) => {
      const next = new Set(prev);
      refs.forEach((r) => { if (select) next.add(r); else next.delete(r); });
      return next;
    });
  };

  const refsPayload = () => [...selectedRefs].map((s) => {
    const i = s.indexOf(":");
    return { kind: s.slice(0, i), id: s.slice(i + 1) };
  });

  const load = useCallback((status, date) => {
    setLoading(true);
    const params = {};
    if (status && status !== "all") params.status = status;
    if (date) params.date = date;
    api.get("/admin/payments", { params })
      .then((r) => setPayments(r.data.payments || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { setSelectedRefs(new Set()); setPreview(null); load(filter, dateFilter); }, [filter, dateFilter, refreshKey, load]);

  const openHistory = async (paymentId) => {
    try {
      const { data } = await api.get(`/admin/payments/${paymentId}/history`);
      setHistory(data.history || []);
      setHistoryReceipts(data.receipts || []);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const review = async (action) => {
    if (acting) return;
    if ((action === "reject" || action === "correction") && notes.trim().length < 5) {
      toast.error("Describe el motivo en las notas (mínimo 5 caracteres).");
      return;
    }
    setActing(true);
    try {
      const { data: rv } = await api.post(`/admin/payments/${selected.id}/review`, { action, notes: notes.trim() || null });
      toast.success(action === "approve" ? "Pago aprobado. Accesos activados." : action === "reject" ? "Pago rechazado." : "Corrección solicitada.");
      if (action === "approve" && rv?.dinner_conflict) {
        toast.warning("La cena no se activó (reserva vencida, liberada o en lista de espera). Revísala en /admin → Cena.", { duration: 8000 });
      }
      setSelected(null);
      setNotes("");
      load(filter);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setActing(false);
    }
  };

  // Vista previa de impacto (debounce) para la barra de acciones masivas.
  useEffect(() => {
    if (!isAdmin || selectedRefs.size === 0) { setPreview(null); return; }
    const t = setTimeout(() => {
      setPreviewLoading(true);
      api.post("/admin/payments/bulk-delete/preview", { payment_refs: refsPayload() })
        .then((r) => setPreview(r.data))
        .catch(() => setPreview(null))
        .finally(() => setPreviewLoading(false));
    }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRefs, isAdmin]);

  const openBulk = () => { setBulkConfirm(""); setBulkAck(false); setBulkResult(null); bulkKeyRef.current = crypto.randomUUID(); setBulkOpen(true); };

  const expectedPhrase = preview ? `ELIMINAR ${preview.units_count} PAGOS Y REGISTROS` : "";

  const doBulkDelete = async () => {
    if (bulkBusy || !preview) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/admin/payments/bulk-delete/execute", {
        payment_refs: refsPayload(),
        confirmation_text: bulkConfirm,
        acknowledged: bulkAck,
        idempotency_key: bulkKeyRef.current,
        selection_hash: preview.selection_hash,
      });
      setBulkResult(data.result);
      setSelectedRefs(new Set());
      setPreview(null);
      load(filter, dateFilter);
      setRefreshKey((k) => k + 1);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : apiError(err));
    } finally {
      setBulkBusy(false);
    }
  };

  const filters = [
    { id: "uploaded", label: "En revisión" },
    { id: "approved", label: "Aprobados" },
    { id: "correction_requested", label: "Corrección solicitada" },
    { id: "rejected", label: "Rechazados" },
    { id: "all", label: "Todos" },
  ];

  return (
    <div data-testid="payments-tab">
      <div className="flex flex-wrap items-center gap-2">
        {filters.map((f) => (
          <button
            key={f.id}
            onClick={() => setFilter(f.id)}
            data-testid={`payments-filter-${f.id}`}
            className={`rounded-full px-4 py-1.5 text-xs font-bold transition-colors ${filter === f.id ? "bg-hz-navy text-white" : "bg-white text-hz-navy border border-hz-gray/50"}`}
          >
            {f.label}
          </button>
        ))}
        <Input type="date" value={dateFilter} onChange={(e) => setDateFilter(e.target.value)} data-testid="payments-date-filter"
          className="w-40 text-xs" aria-label="Filtrar por fecha" />
      </div>

      {inconsistencies.length > 0 && (
        <div className="mt-3 rounded-xl border-2 border-hz-warning/60 bg-hz-warning/10 px-4 py-3" data-testid="payment-inconsistencies-alert">
          <p className="flex items-center gap-2 text-sm font-bold text-hz-navy" data-testid="payment-inconsistencies-count">
            <AlertTriangle className="h-4 w-4 text-hz-warning" />
            {inconsistencies.length} registro(s) con estado de pago inconsistente
          </p>
          <ul className="mt-2 space-y-2">
            {inconsistencies.map((it) => (
              <li key={`${it.folio}-${it.consistency}`} className="flex flex-wrap items-center gap-2 text-xs" data-testid={`inconsistency-${it.folio}`}>
                <span className="font-extrabold tracking-wider text-hz-navy">{it.folio}</span>
                <span className="text-hz-ink/70">{INCONSISTENCY_LABELS[it.consistency] || it.detail}</span>
                {it.consistency === "receipt_without_status" && it.receipt_exists && (
                  <Button size="sm" variant="outline" onClick={() => syncReceiptState(it.folio)} disabled={syncingFolio === it.folio}
                    data-testid={`sync-receipt-${it.folio}`} className="h-7 px-2 text-xs font-bold">
                    {syncingFolio === it.folio ? <Loader2 className="h-3 w-3 animate-spin" /> : "Sincronizar estado del comprobante"}
                  </Button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {isAdmin && selectedRefs.size > 0 && (
        <div className="mt-3 flex flex-wrap items-center gap-2 rounded-xl border-2 border-hz-error/30 bg-hz-error/5 px-4 py-3" data-testid="pay-bulk-action-bar">
          <p className="text-sm font-bold text-hz-navy" data-testid="pay-bulk-selected-count">{selectedRefs.size} pagos seleccionados</p>
          {previewLoading ? <Loader2 className="h-4 w-4 animate-spin text-hz-navy" /> : preview && (
            <p className="text-xs font-semibold text-hz-ink/70" data-testid="pay-bulk-units-counts">
              {preview.units_count} unidades únicas · {preview.impact.adults} adultos · {preview.impact.children} niños
            </p>
          )}
          <div className="ml-auto flex flex-wrap gap-2">
            <Button variant="outline" size="sm" data-testid="pay-bulk-select-all-btn" className="font-semibold"
              onClick={() => toggleMany([...payments.map((p) => `individual:${p.id}`), ...groupIds.map((id) => `group:${id}`)], true)}>
              Seleccionar todos los pagos filtrados
            </Button>
            <Button variant="outline" size="sm" onClick={() => { setSelectedRefs(new Set()); setPreview(null); }} data-testid="pay-bulk-clear-btn" className="font-semibold">Limpiar selección</Button>
            <Button size="sm" onClick={openBulk} disabled={!preview || preview.units_count === 0} data-testid="pay-bulk-delete-open-btn"
              className="bg-hz-error font-bold text-white hover:bg-hz-error/90">
              <Trash2 className="mr-1 h-4 w-4" /> Eliminar pagos y registros relacionados
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : payments.length === 0 ? (
        <p className="py-8 text-sm text-hz-ink/60" data-testid="payments-empty">No hay comprobantes en este estado.</p>
      ) : (
        <div className="mt-4 overflow-x-auto rounded-xl border border-hz-gray/40 bg-white">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                {isAdmin && (
                  <TableHead className="w-10">
                    <Checkbox checked={payments.length > 0 && payments.every((p) => selectedRefs.has(`individual:${p.id}`))}
                      onCheckedChange={(c) => toggleMany(payments.map((p) => `individual:${p.id}`), !!c)}
                      aria-label="Seleccionar todos los pagos de esta página" data-testid="pay-bulk-select-page-checkbox" />
                  </TableHead>
                )}
                <TableHead className="font-bold text-hz-navy">Folio</TableHead>
                <TableHead className="font-bold text-hz-navy">Asistente</TableHead>
                <TableHead className="font-bold text-hz-navy">Monto</TableHead>
                <TableHead className="font-bold text-hz-navy">Estado</TableHead>
                <TableHead className="font-bold text-hz-navy">Fecha</TableHead>
                <TableHead className="font-bold text-hz-navy">Revisado por</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {payments.map((p) => (
                <TableRow key={p.id} onClick={() => { setSelected(p); setNotes(p.review_notes || ""); }} className={`cursor-pointer hover:bg-hz-bg ${selectedRefs.has(`individual:${p.id}`) ? "bg-hz-cyan/5" : ""}`} data-testid={`payment-row-${p.id}`}>
                  {isAdmin && (
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <Checkbox checked={selectedRefs.has(`individual:${p.id}`)} onCheckedChange={() => toggleRef(`individual:${p.id}`)} aria-label={`Seleccionar pago ${p.folio}`} data-testid={`pay-select-${p.id}`} />
                    </TableCell>
                  )}
                  <TableCell className="font-bold text-hz-navy">{p.folio}</TableCell>
                  <TableCell className="font-semibold">{p.attendee_name}</TableCell>
                  <TableCell>{money(p.amount)}</TableCell>
                  <TableCell>
                    <span className={`rounded-full px-3 py-1 text-xs font-bold ${PAYMENT_STATUS[p.status]?.cls || ""}`}>
                      {PAYMENT_STATUS[p.status]?.label || p.status}
                    </span>
                  </TableCell>
                  <TableCell className="text-xs">{new Date(p.created_at).toLocaleString("es-MX")}</TableCell>
                  <TableCell className="text-xs">{p.reviewed_by_name || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <GroupPaymentsQueue readOnly={readOnly} isAdmin={isAdmin} selectedRefs={selectedRefs} onToggleRef={toggleRef} onToggleMany={toggleMany} refreshKey={refreshKey} onLoadedIds={handleGroupLoaded} />

      <Dialog open={!!selected} onOpenChange={(o) => { if (!o) { setSelected(null); setHistory(null); setHistoryReceipts([]); setConfirming(false); } }}>
        <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-1rem)] flex-col gap-0 overflow-hidden bg-white p-0 sm:max-w-2xl">
          <div className="flex-none border-b border-hz-gray/40 px-4 py-3 pr-12 sm:px-6">
            <DialogHeader>
              <DialogTitle className="font-bold text-hz-navy" data-testid="payment-detail-title">
                Revisar pago — Registro individual
              </DialogTitle>
              <p className="text-xs font-semibold text-hz-ink/70">
                Folio <span className="font-extrabold text-hz-navy">{selected?.folio}</span>
                {selected && <> · <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${PAYMENT_STATUS[selected.status]?.cls || ""}`}>{PAYMENT_STATUS[selected.status]?.label || selected.status}</span></>}
              </p>
            </DialogHeader>
          </div>
          <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-6" data-testid="payment-dialog-body">
          {selected && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div><p className="text-xs text-hz-ink/60">Asistente</p><p className="font-bold">{selected.attendee_name}</p></div>
                <div><p className="text-xs text-hz-ink/60">Monto</p><p className="font-bold">{money(selected.amount)}</p></div>
                <div><p className="text-xs text-hz-ink/60">Concepto</p><p className="font-semibold">{selected.concept}</p></div>
                <div><p className="text-xs text-hz-ink/60">Referencia</p><p className="font-semibold">{selected.transfer_reference}</p></div>
              </div>
              {selected.members_breakdown?.length > 0 && (
                <div className="space-y-2" data-testid="payment-breakdown">
                  <p className="text-xs font-bold text-hz-navy">Desglose por adulto (precios históricos)</p>
                  {selected.members_breakdown.map((m) => <MemberBreakdownCard key={m.attendee_id} m={m} defaultOpen />)}
                  <PaymentTotalsCompare expected={selected.members_breakdown.reduce((s, m) => s + m.subtotal, 0)} reported={selected.amount} />
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                <Button variant="outline" size="sm" onClick={() => openHistory(selected.id)} data-testid="payment-history-btn" className="font-semibold">
                  Ver historial del pago
                </Button>
                {canApproveByAccess && !readOnly && (
                  <Button
                    size="sm"
                    onClick={() => setApprovalPaymentId(selected.id)}
                    data-testid="btn-open-approval-modal"
                    className="bg-hz-cyan font-bold text-white hover:bg-hz-blue"
                  >
                    Gestionar aprobación por acceso (pagos parciales)
                  </Button>
                )}
              </div>
              {history && (
                <div className="rounded-lg border border-hz-gray/40" data-testid="payment-history">
                  <p className="border-b border-hz-gray/40 px-3 py-2 text-xs font-bold text-hz-navy">Versiones del comprobante</p>
                  {history.map((v) => (
                    <div key={v.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-1.5 text-xs" data-testid={`payment-history-v${v.version || 1}`}>
                      <span className="font-semibold">v{v.version || 1} · {PAYMENT_STATUS[v.status]?.label || v.status}</span>
                      <span className="text-hz-ink/60">{new Date(v.created_at).toLocaleString("es-MX")}{v.reviewed_by_name ? ` · ${v.reviewed_by_name}` : ""}</span>
                    </div>
                  ))}
                </div>
              )}
              {selected.attendee_photo_url && (
                <div className="flex items-center gap-3 rounded-xl bg-hz-bg p-3" data-testid="payment-attendee-photo-box">
                  <PhotoThumb src={selected.attendee_photo_url} alt={`Fotografía de ${selected.attendee_name}`} className="h-14 w-14 rounded-xl border border-hz-gray/50 object-cover" testId="payment-attendee-photo" />
                  <div>
                    <p className="text-sm font-bold text-hz-ink">{selected.attendee_name}</p>
                    <p className="text-xs text-hz-ink/60">{selected.display_registration_type || "Registro individual"}</p>
                  </div>
                </div>
              )}
              <ReceiptViewer receipt={selected.receipt} versions={historyReceipts} canDownload={!readOnly}
                title={`Comprobante de pago de ${selected.attendee_name}`} testId="receipt-viewer" />
              <TransferAccountAssigner payment={selected} isGroup={false} readOnly={readOnly}
                onSaved={(v) => setSelected((s) => s && ({ ...s, transfer_account_id: v }))} testId="payment-transfer-account" />
              {readOnly && selected.review_notes && (
                <div className="rounded-lg border border-hz-gray/40 bg-hz-bg p-3 text-sm">
                  <p className="text-xs font-semibold text-hz-ink/60">Notas de revisión</p>
                  <p className="mt-1 text-hz-ink">{selected.review_notes}</p>
                </div>
              )}
            </div>
          )}
          </div>
          {selected && !readOnly && (
          <div className="flex-none space-y-3 border-t border-hz-gray/40 bg-white px-4 py-3 sm:px-6" data-testid="payment-dialog-footer">
            <div>
              <Label className="text-xs font-semibold text-hz-ink">Notas de revisión (obligatorias para rechazar o solicitar corrección)</Label>
              <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} data-testid="review-notes-input" className="mt-1" rows={2} placeholder="Ej. El comprobante no muestra la referencia completa…" />
            </div>
            {selected.status !== "approved" && (
              confirming ? (
                <div className="rounded-lg border border-hz-warning/50 bg-hz-warning/5 p-3" data-testid="payment-confirm-approve">
                  <p className="text-xs font-bold text-hz-ink">¿Confirmar aprobación? Se activarán los accesos y el QR del asistente.</p>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Button onClick={() => review("approve")} disabled={acting} data-testid="review-approve-btn" className="bg-hz-success font-bold text-white hover:bg-hz-success/90">
                      {acting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <CheckCircle2 className="mr-1 h-4 w-4" />} Confirmar aprobación
                    </Button>
                    <Button variant="outline" onClick={() => setConfirming(false)} disabled={acting} data-testid="review-confirm-cancel-btn" className="font-bold">Volver</Button>
                  </div>
                </div>
              ) : (
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <Button onClick={() => setConfirming(true)} disabled={acting} data-testid="review-approve-open-btn" className="bg-hz-success font-bold text-white hover:bg-hz-success/90">
                    <CheckCircle2 className="mr-1 h-4 w-4" /> Aprobar pago
                  </Button>
                  <Button onClick={() => review("correction")} disabled={acting} data-testid="review-correction-btn" className="h-auto whitespace-normal bg-hz-warning px-2 text-center text-xs font-bold leading-tight text-white hover:bg-hz-warning/90 sm:text-sm">
                    <RefreshCcw className="mr-1 h-4 w-4" /> Solicitar corrección
                  </Button>
                  <Button onClick={() => review("reject")} disabled={acting} data-testid="review-reject-btn" className="bg-hz-error font-bold text-white hover:bg-hz-error/90">
                    <XCircle className="mr-1 h-4 w-4" /> Rechazar
                  </Button>
                  <Button variant="outline" onClick={() => { setSelected(null); setHistory(null); setHistoryReceipts([]); }} disabled={acting} data-testid="review-cancel-btn" className="font-bold">Cancelar</Button>
                </div>
              )
            )}
          </div>
          )}
        </DialogContent>
      </Dialog>
      <Dialog open={bulkOpen} onOpenChange={(o) => { if (!o && !bulkBusy) setBulkOpen(false); }}>
        <DialogContent className="flex max-h-[calc(100dvh-2rem)] w-[calc(100vw-1rem)] flex-col gap-0 overflow-hidden bg-white p-0 sm:max-w-2xl" data-testid="pay-bulk-modal">
          <div className="flex-none border-b border-hz-gray/40 px-4 py-3 pr-12">
            <DialogHeader>
              <DialogTitle className="font-bold text-hz-error">Eliminación masiva de pagos y registros</DialogTitle>
            </DialogHeader>
          </div>
          <div className="flex-1 overflow-y-auto overflow-x-hidden px-4 py-4">
            {bulkResult ? (
              <div className="space-y-3" data-testid="pay-bulk-result">
                <p className="text-sm font-bold text-hz-navy">Resultado de la eliminación</p>
                <div className="grid grid-cols-2 gap-2 text-center text-xs">
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="pay-bulk-result-deleted">{bulkResult.deleted_units}</span>unidades eliminadas</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-error" data-testid="pay-bulk-result-errors">{bulkResult.errors?.length || 0}</span>errores</div>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <ul className="rounded-lg border border-hz-error/30 bg-hz-error/5 p-3 text-xs text-hz-ink/80" data-testid="pay-bulk-result-error-list">
                    {bulkResult.errors.map((e, i) => <li key={i}>• {e.folio}: {e.error}</li>)}
                  </ul>
                )}
                <Button onClick={() => setBulkOpen(false)} data-testid="pay-bulk-result-close-btn" className="w-full bg-hz-navy font-bold text-white">Cerrar</Button>
              </div>
            ) : !preview ? (
              <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Calculando impacto…</div>
            ) : (
              <div className="space-y-4">
                <p className="rounded-lg border-2 border-hz-error/50 bg-hz-error/5 px-3 py-2 text-xs font-bold text-hz-error" data-testid="pay-bulk-warning">
                  Esta acción eliminará permanentemente los pagos seleccionados y los registros asociados con toda su información relacionada. No podrá deshacerse.
                </p>
                <div className="grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-4" data-testid="pay-bulk-counts">
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="pay-bulk-payments-count">{preview.payments_selected}</span>pago(s) seleccionado(s)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="pay-bulk-units-count">{preview.units_count}</span>unidades únicas</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy">{preview.impact.adults}</span>adultos</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy">{preview.impact.children}</span>niños</div>
                </div>
                <div className="grid grid-cols-2 gap-2 text-xs sm:grid-cols-4" data-testid="pay-bulk-impact">
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.receipts}</b> comprobante(s)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.entitlements}</b> derecho(s)/QR</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.workshop_regs}</b> taller(es)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.check_ins}</b> check-in(s)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.care_movements}</b> mov. cuidado</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.incidents}</b> incidencia(s)</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.notifications}</b> notif./enlaces</div>
                  <div className="rounded-lg bg-hz-bg px-2 py-2"><b>{preview.impact.files}</b> archivo(s)</div>
                </div>
                {preview.units.length > 0 && (
                  <div>
                    <p className="text-xs font-bold text-hz-navy">Unidades que se eliminarán (tipo · folio · contacto · pago · registro)</p>
                    <ul className="mt-1 max-h-36 space-y-1 overflow-y-auto rounded-lg border border-hz-gray/30 p-2 text-xs" data-testid="pay-bulk-units-list">
                      {preview.units.map((u) => (
                        <li key={u.unit_id} className="flex flex-wrap justify-between gap-2">
                          <span className="font-bold text-hz-navy">{u.folio}</span>
                          <span className="text-hz-ink/70">{u.label} · {u.name} · pago: {u.payment_status || "—"} · {u.registration_status || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                <p className="rounded-lg border border-hz-warning/40 bg-hz-warning/5 px-3 py-2 text-xs font-semibold text-hz-ink" data-testid="pay-bulk-approved-warning">
                  Si existen pagos aprobados, comprobantes o asistencias relacionadas, también se eliminarán permanentemente.
                </p>
                <div>
                  <Label className="text-xs font-bold text-hz-ink">Para confirmar, escribe exactamente: <span className="text-hz-error">{expectedPhrase}</span></Label>
                  <Input value={bulkConfirm} onChange={(e) => setBulkConfirm(e.target.value)} placeholder={expectedPhrase}
                    data-testid="pay-bulk-confirm-input" className="mt-1.5" aria-label="Frase de confirmación de eliminación masiva de pagos" />
                </div>
                <label className="flex items-start gap-2 rounded-lg border border-hz-gray/40 p-3 text-xs font-semibold text-hz-ink" data-testid="pay-bulk-ack-label">
                  <Checkbox checked={bulkAck} onCheckedChange={(c) => setBulkAck(!!c)} data-testid="pay-bulk-ack-checkbox" aria-label="Confirmación de eliminación de pagos y registros" />
                  Entiendo que esta acción eliminará permanentemente los pagos y los registros asociados.
                </label>
              </div>
            )}
          </div>
          {!bulkResult && (
            <div className="flex flex-none flex-wrap gap-2 border-t border-hz-gray/40 px-4 py-3">
              <Button variant="outline" onClick={() => setBulkOpen(false)} disabled={bulkBusy} data-testid="pay-bulk-cancel-btn" className="flex-1 font-semibold">Cancelar</Button>
              <Button onClick={doBulkDelete} disabled={bulkBusy || !preview || preview.units_count === 0 || !bulkAck || bulkConfirm.trim() !== expectedPhrase}
                data-testid="pay-bulk-execute-btn" className="flex-1 bg-hz-error font-bold text-white hover:bg-hz-error/90">
                {bulkBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}
                Eliminar permanentemente {preview?.units_count || 0} registros
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* Fase 2B — Modal de aprobación por acceso */}
      <PaymentApprovalModal
        paymentId={approvalPaymentId}
        kind="individual"
        open={!!approvalPaymentId}
        onClose={() => setApprovalPaymentId(null)}
        onUpdated={() => { load(filter, dateFilter); setRefreshKey((k) => k + 1); }}
      />
    </div>
  );
};

export const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null); // {mode:'create'|'edit', user?}
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "usher" });

  const load = useCallback(() => {
    setLoading(true);
    api.get("/admin/users")
      .then((r) => setUsers(r.data.users || []))
      .catch((e) => toast.error(apiError(e)))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    try {
      if (dialog.mode === "create") {
        await api.post("/admin/users", form);
        toast.success("Usuario creado");
      } else {
        const payload = { name: form.name, role: form.role, active: form.active };
        if (form.password) payload.password = form.password;
        await api.patch(`/admin/users/${dialog.user.id}`, payload);
        toast.success("Usuario actualizado");
      }
      setDialog(null);
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  return (
    <div data-testid="users-tab">
      <div className="flex justify-end">
        <Button onClick={() => { setForm({ name: "", email: "", password: "", role: "usher" }); setDialog({ mode: "create" }); }} data-testid="user-add-btn" className="bg-hz-cyan font-bold text-white hover:bg-hz-blue">
          <Plus className="mr-1 h-4 w-4" /> Nuevo usuario
        </Button>
      </div>
      {loading ? (
        <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>
      ) : (
        <div className="mt-4 divide-y divide-hz-gray/30 rounded-xl border border-hz-gray/40 bg-white">
          {users.map((u) => (
            <div key={u.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-3" data-testid={`user-row-${u.email}`}>
              <div>
                <p className="text-sm font-bold text-hz-ink">{u.name} {!u.active && <span className="text-xs font-semibold text-hz-error">(inactivo)</span>}</p>
                <p className="text-xs text-hz-ink/60">{u.email} · {ROLE_LABELS[u.role] || u.role}</p>
              </div>
              <button onClick={() => { setForm({ name: u.name, email: u.email, password: "", role: u.role, active: u.active }); setDialog({ mode: "edit", user: u }); }} data-testid={`user-edit-${u.id}`} className="rounded-md p-1.5 text-hz-navy hover:bg-hz-bg" aria-label={`Editar ${u.name}`}>
                <Pencil className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
      )}

      <Dialog open={!!dialog} onOpenChange={(o) => !o && setDialog(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-bold text-hz-navy">{dialog?.mode === "create" ? "Nuevo usuario" : "Editar usuario"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold">Nombre *</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-form-name" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Correo electrónico *</Label>
              <Input type="email" value={form.email} disabled={dialog?.mode === "edit"} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-form-email" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs font-semibold">{dialog?.mode === "create" ? "Contraseña * (mín. 8 caracteres)" : "Nueva contraseña (opcional)"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="user-form-password" className="mt-1" autoComplete="new-password" />
            </div>
            <div>
              <Label className="text-xs font-semibold">Rol</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="user-form-role" className="mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {Object.entries(ROLE_LABELS).map(([k, v]) => (
                    <SelectItem key={k} value={k} data-testid={`role-option-${k}`}>{v}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {dialog?.mode === "edit" && (
              <label className="flex items-center gap-2 text-sm font-medium text-hz-ink">
                <Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="user-form-active" /> Usuario activo
              </label>
            )}
            <Button onClick={save} data-testid="user-form-save" className="w-full bg-hz-navy font-bold text-white">Guardar</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const PricePrivacyFields = () => {
  const [mode, setMode] = useState("total_only");
  const [note, setNote] = useState("");
  const [savingP, setSavingP] = useState(false);
  useEffect(() => {
    api.get("/admin/site-settings").then((r) => {
      setMode(r.data.settings?.public_price_display_mode || "total_only");
      setNote(r.data.settings?.public_total_note || "");
    }).catch(() => {});
  }, []);
  const savePrivacy = async () => {
    setSavingP(true);
    try {
      await api.put("/admin/site-settings", { public_price_display_mode: mode, public_total_note: note });
      toast.success("Configuración de visibilidad de precios actualizada");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingP(false);
    }
  };
  return (
    <div className="space-y-3 border-t border-hz-gray/40 pt-4" data-testid="price-privacy-section">
      <p className="text-sm font-bold text-hz-navy">Visibilidad de precios al público</p>
      <p className="text-xs text-hz-ink/60">Los asistentes solo ven el total a pagar. El desglose completo sigue disponible para administración y revisión de pagos.</p>
      <div>
        <Label className="text-xs font-semibold">Modo de visualización pública</Label>
        <select value={mode} onChange={(e) => setMode(e.target.value)} data-testid="price-display-mode" className="mt-1 w-full rounded-md border border-hz-gray/50 bg-white px-3 py-2 text-sm">
          <option value="total_only">Solo total general (vigente)</option>
          <option value="itemized" disabled>Desglosado (reservado, no disponible)</option>
        </select>
      </div>
      <div>
        <Label className="text-xs font-semibold">Texto público junto al total</Label>
        <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="public-total-note" className="mt-1" rows={2} />
      </div>
      <Button onClick={savePrivacy} disabled={savingP} variant="outline" data-testid="price-privacy-save" className="font-semibold">
        <Save className="mr-1 h-4 w-4" /> {savingP ? "Guardando…" : "Guardar visibilidad"}
      </Button>
    </div>
  );
};

const DINNER_STATE_LABELS = { available: "Disponible", filling: "Próximo a llenarse", full: "Lleno", closed: "Cerrado manualmente" };
const DINNER_RES_BADGE = {
  reserved: "bg-hz-warning/10 text-hz-warning",
  confirmed: "bg-hz-success/10 text-hz-success",
  waitlisted: "bg-hz-cyan/10 text-hz-cyan",
  released: "bg-hz-gray/30 text-hz-ink/60",
  cancelled: "bg-hz-gray/30 text-hz-ink/60",
};

export const DinnerConfigSection = () => {
  const [cfg, setCfg] = useState(null);
  const [stats, setStats] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/dinner/config")
      .then((r) => { setCfg(r.data.config); setStats(r.data.stats); })
      .catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (k, v) => setCfg((c) => ({ ...c, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      ["dinner_capacity", "dinner_max_per_registration"].forEach((k) => {
        payload[k] = payload[k] === "" || payload[k] === null || payload[k] === undefined ? null : Number(payload[k]);
      });
      payload.dinner_reservation_minutes = Number(payload.dinner_reservation_minutes) || 60;
      payload.dinner_low_inventory_threshold = Number(payload.dinner_low_inventory_threshold ?? 10);
      const { data } = await api.patch("/admin/dinner/config", payload);
      setCfg(data.config);
      setStats(data.stats);
      toast.success("Configuración de cena actualizada");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (!cfg) return <div className="flex items-center gap-2 py-4 text-hz-navy" data-testid="dinner-config-loading"><Loader2 className="h-4 w-4 animate-spin" /> Cargando configuración de cena…</div>;

  const indicators = [
    { label: "Cupo máximo", value: stats.capacity ?? "Sin límite", testId: "dinner-ind-capacity" },
    { label: "Reservados temporalmente", value: stats.reserved, testId: "dinner-ind-reserved" },
    { label: "Confirmados", value: stats.confirmed, testId: "dinner-ind-confirmed" },
    { label: "Disponibles", value: stats.available ?? "—", testId: "dinner-ind-available" },
    { label: "En lista de espera", value: stats.waitlisted, testId: "dinner-ind-waitlisted" },
    { label: "Sobre-cupo por excepción", value: stats.over_capacity, testId: "dinner-ind-over" },
  ];

  return (
    <div className="space-y-4 rounded-xl border border-hz-gray/40 bg-white p-6" data-testid="dinner-config-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-sm font-bold text-hz-navy">Cena Especial — Viernes</p>
          <p className="text-xs text-hz-ink/60">Cupo, reservas temporales, lista de espera y mensajes públicos. El cupo cuenta adultos con cena, nunca grupos.</p>
        </div>
        <span className={`rounded-full px-3 py-1 text-xs font-bold ${stats.state === "available" ? "bg-hz-success/10 text-hz-success" : stats.state === "filling" ? "bg-hz-warning/10 text-hz-warning" : "bg-hz-error/10 text-hz-error"}`} data-testid="dinner-state-badge">
          {DINNER_STATE_LABELS[stats.state] || stats.state}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-3 lg:grid-cols-6" data-testid="dinner-indicators">
        {indicators.map((x) => (
          <div key={x.label} className="rounded-lg bg-hz-bg px-2 py-2">
            <span className="block text-lg font-extrabold text-hz-navy" data-testid={x.testId}>{x.value}</span>
            <span className="text-hz-ink/60">{x.label}</span>
          </div>
        ))}
      </div>

      <div className="grid gap-3 sm:grid-cols-2">
        <label className="flex items-center gap-2 text-xs font-semibold text-hz-ink" data-testid="dinner-enabled-label">
          <Checkbox checked={!!cfg.dinner_enabled} onCheckedChange={(c) => set("dinner_enabled", !!c)} data-testid="dinner-enabled-checkbox" aria-label="Cena habilitada" />
          Cena habilitada
        </label>
        <label className="flex items-center gap-2 text-xs font-semibold text-hz-ink" data-testid="dinner-closed-label">
          <Checkbox checked={!!cfg.dinner_manual_closed} onCheckedChange={(c) => set("dinner_manual_closed", !!c)} data-testid="dinner-closed-checkbox" aria-label="Cierre manual" />
          Cierre manual (bloquea nuevas selecciones)
        </label>
        <label className="flex items-center gap-2 text-xs font-semibold text-hz-ink" data-testid="dinner-reservation-label">
          <Checkbox checked={!!cfg.dinner_reservation_enabled} onCheckedChange={(c) => set("dinner_reservation_enabled", !!c)} data-testid="dinner-reservation-checkbox" aria-label="Reserva temporal habilitada" />
          Reserva temporal desde pre-registro
        </label>
        <label className="flex items-center gap-2 text-xs font-semibold text-hz-ink" data-testid="dinner-waitlist-label">
          <Checkbox checked={!!cfg.dinner_waitlist_enabled} onCheckedChange={(c) => set("dinner_waitlist_enabled", !!c)} data-testid="dinner-waitlist-checkbox" aria-label="Lista de espera habilitada" />
          Lista de espera cuando el cupo esté lleno
        </label>
        <label className="flex items-center gap-2 text-xs font-semibold text-hz-ink" data-testid="dinner-receipt-holds-label">
          <Checkbox checked={!!cfg.dinner_receipt_holds_inventory} onCheckedChange={(c) => set("dinner_receipt_holds_inventory", !!c)} data-testid="dinner-receipt-holds-checkbox" aria-label="Comprobante en revisión conserva el lugar" />
          Comprobante en revisión conserva el lugar
        </label>
        <label className="flex items-center gap-2 text-xs font-semibold text-hz-ink" data-testid="dinner-correction-holds-label">
          <Checkbox checked={!!cfg.dinner_correction_holds_inventory} onCheckedChange={(c) => set("dinner_correction_holds_inventory", !!c)} data-testid="dinner-correction-holds-checkbox" aria-label="Corrección solicitada conserva el lugar" />
          Corrección solicitada conserva el lugar (apagado = se libera)
        </label>
        <div>
          <Label className="text-xs font-semibold">Umbral de "últimos lugares" en pre-registro</Label>
          <Input type="number" min="0" max="100000" value={cfg.dinner_low_inventory_threshold ?? 10} onChange={(e) => set("dinner_low_inventory_threshold", e.target.value)} data-testid="dinner-threshold-input" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Cupo máximo (vacío = sin límite)</Label>
          <Input type="number" min="1" value={cfg.dinner_capacity ?? ""} onChange={(e) => set("dinner_capacity", e.target.value)} data-testid="dinner-capacity-input" className="mt-1" placeholder="Ej. 300" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Duración de reserva temporal (minutos)</Label>
          <Input type="number" min="5" max="2880" value={cfg.dinner_reservation_minutes ?? 60} onChange={(e) => set("dinner_reservation_minutes", e.target.value)} data-testid="dinner-minutes-input" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Máximo de lugares de cena por registro (vacío = sin límite)</Label>
          <Input type="number" min="1" value={cfg.dinner_max_per_registration ?? ""} onChange={(e) => set("dinner_max_per_registration", e.target.value)} data-testid="dinner-max-per-reg-input" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Fecha de la cena</Label>
          <Input type="date" value={cfg.dinner_date || ""} onChange={(e) => set("dinner_date", e.target.value || null)} data-testid="dinner-date-input" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Hora de inicio</Label>
          <Input type="time" value={cfg.dinner_start_time || ""} onChange={(e) => set("dinner_start_time", e.target.value || null)} data-testid="dinner-start-input" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Hora de fin</Label>
          <Input type="time" value={cfg.dinner_end_time || ""} onChange={(e) => set("dinner_end_time", e.target.value || null)} data-testid="dinner-end-input" className="mt-1" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold">Lugar</Label>
          <Input value={cfg.dinner_location || ""} onChange={(e) => set("dinner_location", e.target.value || null)} data-testid="dinner-location-input" className="mt-1" placeholder="Ej. Salón principal" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold">Descripción pública (opcional)</Label>
          <Textarea value={cfg.dinner_description_public || ""} onChange={(e) => set("dinner_description_public", e.target.value || null)} data-testid="dinner-description-input" className="mt-1" placeholder="Texto visible en el registro público" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Mensaje público cuando no hay lugares</Label>
          <Input value={cfg.dinner_full_message_public || ""} onChange={(e) => set("dinner_full_message_public", e.target.value)} data-testid="dinner-full-msg-input" className="mt-1" />
        </div>
        <div>
          <Label className="text-xs font-semibold">Mensaje público de lista de espera</Label>
          <Input value={cfg.dinner_waitlist_message_public || ""} onChange={(e) => set("dinner_waitlist_message_public", e.target.value)} data-testid="dinner-waitlist-msg-input" className="mt-1" />
        </div>
        <div className="sm:col-span-2">
          <Label className="text-xs font-semibold">Notas internas (solo admin)</Label>
          <Textarea value={cfg.dinner_admin_notes || ""} onChange={(e) => set("dinner_admin_notes", e.target.value || null)} data-testid="dinner-notes-input" className="mt-1" />
        </div>
      </div>
      <Button onClick={save} disabled={saving} data-testid="dinner-config-save-btn" className="bg-hz-navy font-bold text-white hover:bg-hz-blue">
        <Save className="mr-1 h-4 w-4" /> {saving ? "Guardando…" : "Guardar configuración de cena"}
      </Button>
    </div>
  );
};

export const DinnerTab = ({ role = "admin" }) => {
  const [data, setData] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [q, setQ] = useState("");
  const [action, setAction] = useState(null);
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(false);
  const [diag, setDiag] = useState(null);
  const [recon, setRecon] = useState(null);
  const [reconAck, setReconAck] = useState(false);
  const [reconBusy, setReconBusy] = useState(false);
  const [coup, setCoup] = useState(null);
  const [coupFix, setCoupFix] = useState(null);
  const [coupAck, setCoupAck] = useState(false);
  const [coupBusy, setCoupBusy] = useState(false);

  const loadCoupled = () => {
    api.get("/admin/diagnostics/coupled-pricing")
      .then((r) => setCoup(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  const dryRunCoupledFix = async () => {
    setCoupBusy(true);
    try {
      const { data: r } = await api.post("/admin/diagnostics/coupled-pricing/fix", { dry_run: true });
      setCoupFix(r);
      setCoupAck(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCoupBusy(false);
    }
  };

  const applyCoupledFix = async () => {
    setCoupBusy(true);
    try {
      const { data: r } = await api.post("/admin/diagnostics/coupled-pricing/fix", { dry_run: false, confirm: true });
      setCoupFix(r);
      toast.success(`Corrección aplicada: ${r.fixed} registros corregidos.`);
      loadCoupled();
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCoupBusy(false);
    }
  };

  const loadDiag = () => {
    api.get("/admin/dinner/inventory-diagnostics")
      .then((r) => setDiag(r.data))
      .catch((e) => toast.error(apiError(e)));
  };

  const dryRunReconcile = async () => {
    setReconBusy(true);
    try {
      const { data: r } = await api.post("/admin/dinner/reconcile-inventory", { dry_run: true });
      setRecon(r);
      setReconAck(false);
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setReconBusy(false);
    }
  };

  const applyReconcile = async () => {
    setReconBusy(true);
    try {
      const { data: r } = await api.post("/admin/dinner/reconcile-inventory", { dry_run: false, confirm: true });
      setRecon(r);
      toast.success(`Reconciliación aplicada: ${r.created} reservas históricas creadas.`);
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setReconBusy(false);
    }
  };

  const load = useCallback(() => {
    const params = {};
    if (statusFilter) params.status = statusFilter;
    if (q.trim()) params.q = q.trim();
    api.get("/admin/dinner/reservations", { params })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(apiError(e)));
  }, [statusFilter, q]);
  useEffect(() => { load(); }, [load]);

  const doAction = async () => {
    if (!action || acting) return;
    setActing(true);
    try {
      const { data: r } = await api.post(`/admin/dinner/reservations/${action.res.id}/${action.type}`, { reason: reason.trim() || null });
      toast.success(action.type === "promote"
        ? (r.exception ? "Solicitud promovida con excepción de cupo (auditada). Confirma el cobro adicional manualmente." : "Solicitud promovida y confirmada.")
        : "Reserva liberada; el cupo quedó disponible.");
      setAction(null);
      setReason("");
      load();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setActing(false);
    }
  };

  const s = data?.stats;
  return (
    <div className="space-y-4" data-testid="dinner-tab">
      {s && (
        <div className="grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-3 lg:grid-cols-6" data-testid="dinner-tab-stats">
          <div className="rounded-lg bg-white px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="dinner-tab-capacity">{s.capacity ?? "∞"}</span>Cupo</div>
          <div className="rounded-lg bg-white px-2 py-2"><span className="block text-lg font-extrabold text-hz-warning" data-testid="dinner-tab-reserved">{s.reserved}</span>Reservados</div>
          <div className="rounded-lg bg-white px-2 py-2"><span className="block text-lg font-extrabold text-hz-success" data-testid="dinner-tab-confirmed">{s.confirmed}</span>Confirmados</div>
          <div className="rounded-lg bg-white px-2 py-2"><span className="block text-lg font-extrabold text-hz-cyan" data-testid="dinner-tab-waitlisted">{s.waitlisted}</span>En espera</div>
          <div className="rounded-lg bg-white px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="dinner-tab-available">{s.available ?? "—"}</span>Disponibles</div>
          <div className="rounded-lg bg-white px-2 py-2"><span className="block text-lg font-extrabold text-hz-error" data-testid="dinner-tab-over">{s.over_capacity}</span>Sobre-cupo</div>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar por nombre o folio…" data-testid="dinner-search-input" className="w-64" aria-label="Buscar en lista de cena" />
        <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
          <SelectTrigger className="w-52" data-testid="dinner-status-filter" aria-label="Filtrar por estado de reserva"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all" data-testid="dinner-status-all">Todos los estados</SelectItem>
            <SelectItem value="reserved" data-testid="dinner-status-reserved">Reservados temporales</SelectItem>
            <SelectItem value="confirmed" data-testid="dinner-status-confirmed">Confirmados</SelectItem>
            <SelectItem value="waitlisted" data-testid="dinner-status-waitlisted">En lista de espera</SelectItem>
            <SelectItem value="released" data-testid="dinner-status-released">Liberados</SelectItem>
            <SelectItem value="cancelled" data-testid="dinner-status-cancelled">Cancelados</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" size="sm" onClick={load} data-testid="dinner-refresh-btn" className="font-semibold"><RefreshCcw className="mr-1 h-4 w-4" /> Actualizar</Button>
        <Button variant="outline" size="sm" onClick={loadDiag} data-testid="dinner-diagnostics-btn" className="font-semibold">Diagnóstico</Button>
        <Button variant="outline" size="sm" onClick={dryRunReconcile} disabled={reconBusy} data-testid="dinner-reconcile-btn" className="font-semibold">Reconciliar inventario</Button>
      </div>
      {diag && (
        <div className="space-y-3 rounded-xl border border-hz-gray/40 bg-white p-4" data-testid="dinner-diagnostics-panel">
          <div className="flex items-center justify-between">
            <p className="text-sm font-bold text-hz-navy">Diagnóstico de inventario — {diag.label}</p>
            <Button variant="ghost" size="sm" onClick={() => setDiag(null)} data-testid="dinner-diagnostics-close" className="text-xs">Cerrar</Button>
          </div>
          <div className="grid grid-cols-2 gap-2 text-center text-xs sm:grid-cols-4 lg:grid-cols-8" data-testid="dinner-diagnostics-counts">
            <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="diag-capacity">{diag.capacity_total ?? "∞"}</span>Cupo</div>
            <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-success" data-testid="diag-confirmed">{diag.confirmed_count}</span>Confirmados</div>
            <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-warning" data-testid="diag-held">{diag.held_count}</span>Reservados</div>
            <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-navy" data-testid="diag-available">{diag.available_count ?? "—"}</span>Disponibles</div>
            <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-cyan" data-testid="diag-courtesy">{diag.complimentary_count}</span>Cortesías</div>
            <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-cyan" data-testid="diag-historical">{diag.historical_count}</span>Históricos</div>
            <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-ink" data-testid="diag-released">{diag.released_count}</span>Liberados</div>
            <div className="rounded-lg bg-hz-bg px-2 py-2"><span className="block text-lg font-extrabold text-hz-error" data-testid="diag-over">{diag.over_capacity}</span>Sobre-cupo</div>
          </div>
          <p className="text-xs text-hz-ink/60" data-testid="diag-last-reconciled">
            Última reconciliación: {diag.last_reconciled_at ? new Date(diag.last_reconciled_at).toLocaleString("es-MX") : "nunca"} · Por fuente: {Object.entries(diag.by_source || {}).map(([k, v]) => `${k}: ${v}`).join(" · ") || "—"}
          </p>
          <div className="space-y-1 text-xs" data-testid="dinner-diagnostics-inconsistencies">
            {[
              ["entitlement_sin_reserva", "Acceso de cena sin reserva de inventario"],
              ["reserva_sin_asistente", "Reservas sin asistente válido"],
              ["cancelados_que_descuentan", "Registros cancelados que aún descuentan cupo"],
              ["cortesias_sin_reserva", "Cortesías sin reserva confirmada"],
              ["confirmadas_sin_derecho", "Reservas confirmadas sin derecho activo"],
              ["duplicados", "Duplicados por asistente"],
            ].map(([key, label]) => {
              const items = diag.inconsistencies?.[key] || [];
              return (
                <p key={key} className={items.length ? "font-semibold text-hz-error" : "text-hz-ink/50"} data-testid={`diag-inc-${key}`}>
                  {label}: {items.length}{items.length ? ` — ${items.map((x) => x.folio || x.reservation_id).slice(0, 10).join(", ")}${items.length > 10 ? "…" : ""}` : ""}
                </p>
              );
            })}
          </div>
        </div>
      )}
      <div className="overflow-x-auto rounded-xl border border-hz-gray/40 bg-white">
        <Table>
          <TableHeader>
            <TableRow className="bg-slate-50">
              <TableHead className="font-bold text-hz-navy">Folio</TableHead>
              <TableHead className="font-bold text-hz-navy">Nombre</TableHead>
              <TableHead className="font-bold text-hz-navy">Organización</TableHead>
              <TableHead className="font-bold text-hz-navy">Tipo</TableHead>
              <TableHead className="font-bold text-hz-navy">Estado</TableHead>
              <TableHead className="font-bold text-hz-navy">Fuente</TableHead>
              <TableHead className="font-bold text-hz-navy">Reservada</TableHead>
              <TableHead className="font-bold text-hz-navy">Check-in</TableHead>
              <TableHead className="font-bold text-hz-navy">Acciones</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(data?.reservations || []).map((r) => (
              <TableRow key={r.id} data-testid={`dinner-row-${r.id}`}>
                <TableCell className="font-bold text-hz-navy">{r.attendee_folio}</TableCell>
                <TableCell>{r.attendee_name}</TableCell>
                <TableCell className="text-xs">{[r.delegation_name, r.sector_name].filter(Boolean).join(" / ")}</TableCell>
                <TableCell className="text-xs">{{ individual: "Individual", family: "Familiar", group: "Grupal" }[r.group_type] || "—"}</TableCell>
                <TableCell>
                  <span className={`rounded-full px-2 py-0.5 text-xs font-bold ${DINNER_RES_BADGE[r.status] || ""}`} data-testid={`dinner-row-status-${r.id}`}>
                    {r.status_label}{r.exception_reason ? " (excepción)" : ""}
                  </span>
                </TableCell>
                <TableCell className="text-xs">{{ registration: "Registro", manual: "Manual", import: "Importación", courtesy: "Cortesía", admin_exception: "Excepción admin" }[r.source] || r.source}</TableCell>
                <TableCell className="text-xs">{r.reserved_at ? new Date(r.reserved_at).toLocaleString("es-MX") : "—"}{r.expires_at && r.status === "reserved" ? <span className="block text-hz-warning">vence {new Date(r.expires_at).toLocaleString("es-MX")}</span> : null}</TableCell>
                <TableCell className="text-xs">{r.checked_in_at ? new Date(r.checked_in_at).toLocaleString("es-MX") : "—"}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {r.status === "waitlisted" && role === "admin" && (
                      <Button size="sm" variant="outline" onClick={() => { setAction({ type: "promote", res: r }); setReason(""); }} data-testid={`dinner-promote-${r.id}`} className="font-semibold">Promover</Button>
                    )}
                    {["reserved", "confirmed", "waitlisted"].includes(r.status) && (role === "admin" || r.source === "courtesy") && (
                      <Button size="sm" variant="outline" onClick={() => { setAction({ type: "release", res: r }); setReason(""); }} data-testid={`dinner-release-${r.id}`} className="font-semibold text-hz-error">Liberar</Button>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {data && data.reservations.length === 0 && <p className="p-6 text-center text-sm text-hz-ink/60" data-testid="dinner-empty">Sin reservas de cena para los filtros actuales.</p>}
      </div>

      <Dialog open={!!recon} onOpenChange={(o) => { if (!o && !reconBusy) setRecon(null); }}>
        <DialogContent className="bg-white" data-testid="dinner-reconcile-modal">
          <DialogHeader>
            <DialogTitle className="font-bold text-hz-navy">Reconciliar inventario de cena</DialogTitle>
          </DialogHeader>
          {recon && (
            <div className="space-y-3 text-xs">
              {recon.dry_run ? (
                <>
                  <p className="text-hz-ink/70" data-testid="reconcile-dryrun-summary">
                    Análisis sin cambios: se detectaron <b>{recon.detected}</b> accesos de cena vigentes sin reserva de inventario
                    ({recon.courtesy_fixes} cortesías, {recon.historical_fixes} históricos). Se crearían {recon.would_create} reservas confirmadas;
                    duplicados ignorados: {recon.duplicates_ignored}; estados inválidos: {recon.invalid_states}.
                  </p>
                  {(recon.details || []).length > 0 && (
                    <p className="max-h-32 overflow-y-auto rounded-lg bg-hz-bg p-2" data-testid="reconcile-dryrun-details">
                      {recon.details.map((d) => `${d.folio} (${d.source})`).join(" · ")}
                    </p>
                  )}
                  <p className="text-hz-ink/60">Al aplicar: no se aprueban pagos ni se activan accesos; solo se contabiliza el inventario y cada cambio queda auditado.</p>
                  <label className="flex items-start gap-2 font-semibold text-hz-ink" data-testid="reconcile-confirm-label">
                    <Checkbox checked={reconAck} onCheckedChange={(c) => setReconAck(!!c)} data-testid="reconcile-confirm-checkbox" aria-label="Confirmar reconciliación" />
                    Confirmo que deseo crear las reservas históricas faltantes (operación idempotente y auditada).
                  </label>
                  <div className="flex gap-2">
                    <Button variant="outline" onClick={() => setRecon(null)} disabled={reconBusy} data-testid="reconcile-cancel-btn" className="flex-1 font-semibold">Cancelar</Button>
                    <Button onClick={applyReconcile} disabled={reconBusy || !reconAck || recon.detected === 0} data-testid="reconcile-apply-btn" className="flex-1 bg-hz-navy font-bold text-white hover:bg-hz-blue">
                      {reconBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Aplicar reconciliación
                    </Button>
                  </div>
                </>
              ) : (
                <>
                  <p className="font-semibold text-hz-success" data-testid="reconcile-result-summary">
                    Reconciliación aplicada (ejecución {recon.run_id}): {recon.analyzed} analizados · {recon.created} creados · {recon.skipped_existing} omitidos · {recon.errors} errores.
                  </p>
                  {(recon.details || []).length > 0 && (
                    <p className="max-h-32 overflow-y-auto rounded-lg bg-hz-bg p-2" data-testid="reconcile-result-details">
                      {recon.details.map((d) => `${d.folio} (${d.source})`).join(" · ")}
                    </p>
                  )}
                  <Button onClick={() => setRecon(null)} data-testid="reconcile-close-btn" className="w-full bg-hz-navy font-bold text-white hover:bg-hz-blue">Cerrar</Button>
                </>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {role === "admin" && (
        <div className="space-y-3 rounded-xl border border-hz-gray/40 bg-white p-4" data-testid="coupled-pricing-card">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <p className="text-sm font-bold text-hz-navy">Acople histórico cena → evento general (cargo indebido del sábado)</p>
              <p className="text-xs text-hz-ink/60">Detecta registros con cena + evento general sin talleres (regla anterior). Pendientes: se corrige el total. En revisión: se marca la discrepancia sin tocar el monto reportado. Aprobados: solo se reportan.</p>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={loadCoupled} data-testid="coupled-detect-btn" className="font-semibold">Detectar registros</Button>
              <Button variant="outline" size="sm" onClick={dryRunCoupledFix} disabled={coupBusy} data-testid="coupled-fix-btn" className="font-semibold">Corregir</Button>
            </div>
          </div>
          {coup && (
            <div className="space-y-1 text-xs" data-testid="coupled-panel">
              <p className="font-semibold text-hz-ink" data-testid="coupled-summary">
                {coup.total} registro(s) posibles — pendientes: {coup.por_clasificacion?.pendiente || 0} · en revisión: {coup.por_clasificacion?.en_revision || 0} · aprobados (sin tocar): {coup.por_clasificacion?.aprobado || 0}
              </p>
              {(coup.registros || []).slice(0, 20).map((r) => (
                <p key={r.attendee_id} className="text-hz-ink/70" data-testid={`coupled-row-${r.folio}`}>
                  {r.folio} · {r.full_name} · cobrado ${r.charged_total} · correcto ${r.expected_total} · diferencia ${r.difference} · {r.classification}
                </p>
              ))}
            </div>
          )}
        </div>
      )}

      <Dialog open={!!coupFix} onOpenChange={(o) => { if (!o && !coupBusy) setCoupFix(null); }}>
        <DialogContent className="bg-white" data-testid="coupled-fix-modal">
          <DialogHeader>
            <DialogTitle className="font-bold text-hz-navy">Corregir cargo indebido del sábado</DialogTitle>
          </DialogHeader>
          {coupFix && (coupFix.dry_run ? (
            <div className="space-y-3 text-xs">
              <p className="text-hz-ink/70" data-testid="coupled-fix-summary">
                {coupFix.candidatos} registro(s) con posible cargo indebido — se corregirán {coupFix.por_clasificacion?.pendiente || 0} pendientes, se marcarán {coupFix.por_clasificacion?.en_revision || 0} en revisión (sin tocar el monto reportado) y se omitirán {coupFix.por_clasificacion?.aprobado || 0} aprobados.
              </p>
              <p className="text-hz-ink/60">La corrección quita el evento general del desglose y del total esperado, revoca el acceso pendiente del sábado y audita cada cambio. No toca QR, accesos activos ni cupo de cena.</p>
              <label className="flex items-start gap-2 font-semibold text-hz-ink" data-testid="coupled-fix-confirm-label">
                <Checkbox checked={coupAck} onCheckedChange={(c) => setCoupAck(!!c)} data-testid="coupled-fix-confirm-checkbox" aria-label="Confirmar corrección de precios" />
                Confirmo la corrección de los registros pendientes y el marcado de discrepancias (operación idempotente y auditada).
              </label>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setCoupFix(null)} disabled={coupBusy} data-testid="coupled-fix-cancel-btn" className="flex-1 font-semibold">Cancelar</Button>
                <Button onClick={applyCoupledFix} disabled={coupBusy || !coupAck || (coupFix.candidatos || 0) === 0} data-testid="coupled-fix-apply-btn" className="flex-1 bg-hz-navy font-bold text-white hover:bg-hz-blue">
                  {coupBusy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}Aplicar corrección
                </Button>
              </div>
            </div>
          ) : (
            <div className="space-y-3 text-xs">
              <p className="font-semibold text-hz-success" data-testid="coupled-fix-result">
                Corrección aplicada: {coupFix.fixed} corregidos · {coupFix.flagged} marcados en revisión · {coupFix.skipped} omitidos.
              </p>
              <Button onClick={() => setCoupFix(null)} data-testid="coupled-fix-close-btn" className="w-full bg-hz-navy font-bold text-white hover:bg-hz-blue">Cerrar</Button>
            </div>
          ))}
        </DialogContent>
      </Dialog>

      <Dialog open={!!action} onOpenChange={(o) => { if (!o && !acting) setAction(null); }}>
        <DialogContent className="bg-white" data-testid="dinner-action-dialog">
          <DialogHeader>
            <DialogTitle className="font-bold text-hz-navy">
              {action?.type === "promote" ? `Promover de lista de espera — ${action?.res?.attendee_folio}` : `Liberar reserva de cena — ${action?.res?.attendee_folio}`}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs text-hz-ink/70">
              {action?.type === "promote"
                ? "Si hay cupo disponible se confirma directamente. Si la cena está llena, escribe un motivo para aplicar una excepción de sobre-cupo (queda auditada). El cobro adicional se confirma manualmente fuera de la app."
                : "Se liberará el lugar de cena, se revocará el derecho de acceso/QR de cena y quedará auditado."}
            </p>
            <Label className="text-xs font-semibold">{action?.type === "promote" ? "Motivo de excepción (opcional si hay cupo)" : "Motivo (obligatorio)"}</Label>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} data-testid="dinner-action-reason" placeholder={action?.type === "promote" ? "Solo si quieres exceder el cupo…" : "Ej. Solicitud del asistente…"} />
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setAction(null)} disabled={acting} data-testid="dinner-action-cancel" className="flex-1 font-semibold">Cancelar</Button>
              <Button onClick={doAction} disabled={acting || (action?.type === "release" && reason.trim().length < 5) || (action?.type === "promote" && reason.trim().length > 0 && reason.trim().length < 5)}
                data-testid="dinner-action-confirm" className="flex-1 bg-hz-navy font-bold text-white hover:bg-hz-blue">
                {acting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                {action?.type === "promote" ? "Promover" : "Liberar reserva"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export const SettingsTab = () => (
  <div className="max-w-2xl space-y-4" data-testid="settings-tab">
    <TransferAccountsManager />
    <div className="space-y-4 rounded-xl border border-hz-gray/40 bg-white p-6">
      <DinnerConfigSection />
      <PricePrivacyFields />
    </div>
  </div>
);

const EMPTY_ACCOUNT_FORM = { label: "", bank_name: "", account_holder: "", clabe: "", account_number: "", card_number: "", instructions: "", is_active: true, is_primary: false, sort_order: 0 };

const TransferAccountsManager = () => {
  const [accounts, setAccounts] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    api.get("/admin/transfer-accounts").then((r) => setAccounts(r.data.accounts || [])).catch((e) => toast.error(apiError(e)));
  }, []);
  useEffect(() => { load(); }, [load]);

  if (!accounts) return <div className="flex items-center gap-2 py-8 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando…</div>;
  const activeCount = accounts.filter((a) => a.is_active).length;

  const openNew = () => setForm({ ...EMPTY_ACCOUNT_FORM, sort_order: accounts.length + 1 });
  const openEdit = (a) => setForm({
    id: a.id, label: a.label || "", bank_name: a.bank_name || "", account_holder: a.account_holder || "",
    clabe: a.clabe || "", account_number: a.account_number || "", card_number: a.card_number || "",
    instructions: a.instructions || "", is_active: !!a.is_active, is_primary: !!a.is_primary, sort_order: a.sort_order || 0,
  });

  const accountPayload = (src, flags = {}) => ({
    label: src.label, bank_name: src.bank_name || null, account_holder: src.account_holder || null,
    clabe: src.clabe || null, account_number: src.account_number || null, card_number: src.card_number || null,
    instructions: src.instructions || null, is_active: !!src.is_active, is_primary: !!src.is_primary,
    sort_order: Number(src.sort_order) || 0, ...flags,
  });

  const save = async () => {
    setSaving(true);
    try {
      if (form.id) await api.patch(`/admin/transfer-accounts/${form.id}`, accountPayload(form));
      else await api.post("/admin/transfer-accounts", accountPayload(form));
      toast.success("Cuenta guardada");
      setForm(null);
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : apiError(err));
    } finally { setSaving(false); }
  };

  const patchFlags = async (a, flags, warn) => {
    if (warn && !window.confirm(warn)) return;
    try {
      await api.patch(`/admin/transfer-accounts/${a.id}`, accountPayload(a, flags));
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : apiError(err));
    }
  };

  const move = async (idx, dir) => {
    const ordered = [...accounts];
    const j = idx + dir;
    if (j < 0 || j >= ordered.length) return;
    [ordered[idx], ordered[j]] = [ordered[j], ordered[idx]];
    try {
      await api.post("/admin/transfer-accounts/reorder", { ordered_ids: ordered.map((a) => a.id) });
      load();
    } catch (err) { toast.error(apiError(err)); }
  };

  const remove = async (a) => {
    if (!window.confirm(`¿Eliminar la cuenta "${a.label}"? Solo puedes eliminar cuentas sin pagos conciliados.`)) return;
    try {
      await api.delete(`/admin/transfer-accounts/${a.id}`);
      toast.success("Cuenta eliminada");
      load();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : apiError(err));
    }
  };

  return (
    <div className="rounded-xl border border-hz-gray/40 bg-white p-6" data-testid="transfer-accounts-manager">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-bold text-hz-navy">Cuentas bancarias para transferencia</p>
          <p className="text-xs text-hz-ink/60">Hasta 2 cuentas activas. Se muestran al asistente solo cuando tiene pago pendiente, rechazado o corrección solicitada.</p>
        </div>
        <Button size="sm" onClick={openNew} data-testid="transfer-account-new-btn" className="bg-hz-navy font-bold text-white hover:bg-hz-blue">
          <Plus className="mr-1 h-4 w-4" /> Agregar cuenta
        </Button>
      </div>

      {activeCount === 0 && (
        <p className="mt-3 rounded-lg border border-hz-warning/50 bg-hz-warning/10 px-3 py-2 text-xs font-semibold text-hz-ink" data-testid="transfer-accounts-empty-warning">
          No hay cuentas configuradas para recibir transferencias. Los asistentes no podrán ver instrucciones de pago.
        </p>
      )}

      <div className="mt-4 space-y-3">
        {accounts.map((a, i) => (
          <div key={a.id} className="rounded-xl border border-hz-gray/40 p-3" data-testid={`transfer-account-card-${a.id}`}>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2">
                <p className="text-sm font-extrabold text-hz-navy">{a.label}</p>
                {a.is_primary && <span className="rounded-full bg-hz-cyan/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-hz-cyan">Cuenta principal</span>}
                {a.is_active
                  ? <span className="rounded-full bg-hz-success/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-hz-success" title="Visible para asistentes">Activa · Visible para asistentes</span>
                  : <span className="rounded-full bg-hz-gray/30 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-hz-ink/50">Inactiva</span>}
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <Button size="sm" variant="ghost" onClick={() => move(i, -1)} disabled={i === 0} data-testid={`transfer-account-up-${a.id}`} aria-label="Subir"><ChevronUp className="h-4 w-4" /></Button>
                <Button size="sm" variant="ghost" onClick={() => move(i, 1)} disabled={i === accounts.length - 1} data-testid={`transfer-account-down-${a.id}`} aria-label="Bajar"><ChevronDown className="h-4 w-4" /></Button>
                {!a.is_primary && a.is_active && (
                  <Button size="sm" variant="outline" onClick={() => patchFlags(a, { is_primary: true })} data-testid={`transfer-account-primary-${a.id}`} className="text-xs font-bold">Hacer principal</Button>
                )}
                {a.is_active
                  ? <Button size="sm" variant="outline" onClick={() => patchFlags(a, { is_active: false }, activeCount === 1 ? "Es la única cuenta activa: los asistentes no podrán ver instrucciones de pago. ¿Continuar?" : null)} data-testid={`transfer-account-toggle-${a.id}`} className="text-xs font-bold">Desactivar</Button>
                  : <Button size="sm" variant="outline" onClick={() => patchFlags(a, { is_active: true })} data-testid={`transfer-account-toggle-${a.id}`} className="text-xs font-bold">Activar</Button>}
                <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`transfer-account-edit-${a.id}`} className="text-xs font-bold"><Pencil className="mr-1 h-3 w-3" /> Editar</Button>
                <Button size="sm" variant="outline" onClick={() => remove(a)} data-testid={`transfer-account-delete-${a.id}`} className="text-xs font-bold text-hz-error"><Trash2 className="h-3 w-3" /></Button>
              </div>
            </div>
            <p className="mt-1 text-xs text-hz-ink/60">
              {[a.bank_name && `Banco: ${a.bank_name}`, a.account_holder && `Titular: ${a.account_holder}`, a.clabe && `CLABE: ${a.clabe}`, a.account_number && `Cuenta: ${a.account_number}`, a.card_number && `Tarjeta: ${a.card_number}`].filter(Boolean).join(" · ")}
            </p>
          </div>
        ))}
        {accounts.length === 0 && <p className="text-xs text-hz-ink/50">Aún no hay cuentas. Agrega la primera.</p>}
      </div>

      {form && (
        <div className="mt-4 space-y-3 rounded-xl border-2 border-hz-cyan/40 bg-hz-cyan/5 p-4" data-testid="transfer-account-form">
          <p className="text-sm font-bold text-hz-navy">{form.id ? "Editar cuenta" : "Nueva cuenta"}</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <div><Label className="text-xs font-semibold">Etiqueta visible *</Label>
              <Input value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} data-testid="ta-label-input" className="mt-1" placeholder='Ej. "Opción 1", "Cuenta Iglesia"' /></div>
            <div><Label className="text-xs font-semibold">Banco</Label>
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="ta-bank-input" className="mt-1" placeholder="Ej. BBVA" /></div>
            <div><Label className="text-xs font-semibold">Titular</Label>
              <Input value={form.account_holder} onChange={(e) => setForm({ ...form, account_holder: e.target.value })} data-testid="ta-holder-input" className="mt-1" placeholder="Nombre del titular" /></div>
            <div><Label className="text-xs font-semibold">CLABE (18 dígitos)</Label>
              <Input value={form.clabe} onChange={(e) => setForm({ ...form, clabe: e.target.value })} data-testid="ta-clabe-input" className="mt-1" placeholder="18 dígitos" /></div>
            <div><Label className="text-xs font-semibold">Número de cuenta (opcional)</Label>
              <Input value={form.account_number} onChange={(e) => setForm({ ...form, account_number: e.target.value })} data-testid="ta-account-number-input" className="mt-1" /></div>
            <div><Label className="text-xs font-semibold">Número de tarjeta (opcional)</Label>
              <Input value={form.card_number} onChange={(e) => setForm({ ...form, card_number: e.target.value })} data-testid="ta-card-input" className="mt-1" /></div>
          </div>
          <div><Label className="text-xs font-semibold">Instrucciones adicionales (opcional)</Label>
            <Textarea value={form.instructions} onChange={(e) => setForm({ ...form, instructions: e.target.value })} data-testid="ta-instructions-input" className="mt-1" placeholder="Ej. Envía tu comprobante el mismo día de tu transferencia…" /></div>
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="ta-active-check" /> Activa</label>
            <label className="flex items-center gap-2 font-semibold"><input type="checkbox" checked={form.is_primary} onChange={(e) => setForm({ ...form, is_primary: e.target.checked })} data-testid="ta-primary-check" /> Principal</label>
            <label className="flex items-center gap-2 font-semibold">Orden
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: e.target.value })} data-testid="ta-sort-input" className="w-20" /></label>
          </div>
          <div className="flex gap-2">
            <Button onClick={save} disabled={saving} data-testid="transfer-account-save-btn" className="bg-hz-navy font-bold text-white hover:bg-hz-blue">
              <Save className="mr-1 h-4 w-4" /> {saving ? "Guardando…" : "Guardar cuenta"}
            </Button>
            <Button variant="outline" onClick={() => setForm(null)} data-testid="transfer-account-cancel-btn" className="font-bold">Cancelar</Button>
          </div>
        </div>
      )}

      {activeCount > 0 && (
        <div className="mt-4" data-testid="transfer-accounts-preview">
          <p className="mb-2 text-xs font-bold uppercase tracking-wide text-hz-ink/50">Vista previa pública</p>
          <TransferAccountsCard accounts={accounts.filter((a) => a.is_active).map((a) => ({ id: a.id, label: a.label, bank_name: a.bank_name, account_holder: a.account_holder, clabe: a.clabe, account_number: a.account_number, card_number: a.card_number, instructions: a.instructions, is_primary: a.is_primary }))} folio="HZ26-000000" testId="ta-preview-card" />
        </div>
      )}
    </div>
  );
};

const TransferAccountAssigner = ({ payment, isGroup, readOnly, onSaved, testId }) => {
  const [accounts, setAccounts] = useState([]);
  const [sel, setSel] = useState(payment?.transfer_account_id || "");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/admin/transfer-accounts").then((r) => setAccounts(r.data.accounts || [])).catch(() => {});
  }, []);
  useEffect(() => { setSel(payment?.transfer_account_id || ""); }, [payment?.id, payment?.transfer_account_id]);

  if (!payment) return null;

  const saveAssignment = async () => {
    setSaving(true);
    try {
      await api.post(`/admin/${isGroup ? "group-payments" : "payments"}/${payment.id}/transfer-account`, { transfer_account_id: sel || null });
      toast.success("Cuenta receptora actualizada");
      onSaved?.(sel || null);
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === "string" ? d : apiError(err));
    } finally { setSaving(false); }
  };

  return (
    <div className="rounded-xl border border-hz-gray/30 bg-hz-bg/50 px-3 py-2.5" data-testid={testId}>
      <Label className="text-xs font-bold text-hz-navy">Cuenta receptora utilizada (interno)</Label>
      <div className="mt-1 flex items-center gap-2">
        <select value={sel} onChange={(e) => setSel(e.target.value)} disabled={readOnly} data-testid={`${testId}-select`}
          className="w-full rounded-lg border border-hz-gray/50 bg-white px-2 py-1.5 text-sm">
          <option value="">No identificada</option>
          {accounts.map((a) => <option key={a.id} value={a.id}>{a.label}</option>)}
        </select>
        {!readOnly && (
          <Button size="sm" variant="outline" onClick={saveAssignment} disabled={saving} data-testid={`${testId}-save`} className="text-xs font-bold">
            {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : "Guardar cuenta"}
          </Button>
        )}
      </div>
      {payment.transfer_account_label && (
        <p className="mt-1 text-xs text-hz-ink/60" data-testid={`${testId}-current`}>Actual: {payment.transfer_account_label}</p>
      )}
    </div>
  );
};

const ENT_STATUS = {
  active: { label: "Activo", cls: "bg-hz-success/10 text-hz-success" },
  pending: { label: "Pendiente", cls: "bg-hz-warning/10 text-hz-warning" },
  revoked: { label: "Revocado", cls: "bg-hz-error/10 text-hz-error" },
};
const SOURCE_LABELS = { registration: "Registro", payment: "Pago", courtesy: "Cortesía", admin: "Admin" };

export const EntitlementsPanel = ({ attendeeId, onChanged, role = "admin" }) => {
  const [ents, setEnts] = useState(null);
  const [availableCents, setAvailableCents] = useState(0);
  const [workshops, setWorkshops] = useState([]);
  const [cType, setCType] = useState("general_event");
  const [cWorkshop, setCWorkshop] = useState("");
  const [reason, setReason] = useState("");
  const [acting, setActing] = useState(false);
  const [dinnerInfo, setDinnerInfo] = useState(null);
  const [dinnerOverride, setDinnerOverride] = useState(false);
  const [modOpen, setModOpen] = useState(false);

  useEffect(() => {
    if (cType !== "dinner") { setDinnerInfo(null); setDinnerOverride(false); return; }
    setDinnerInfo(null);
    api.get("/admin/dinner/config")
      .then((r) => setDinnerInfo(r.data.stats))
      .catch((e) => setDinnerInfo({ forbidden: e?.response?.status === 403 }));
  }, [cType]);

  const load = useCallback(() => {
    api.get(`/admin/attendees/${attendeeId}/entitlements`)
      .then((r) => {
        setEnts(r.data.entitlements || []);
        setAvailableCents(r.data.available_cents || 0);
      })
      .catch((e) => toast.error(apiError(e)));
  }, [attendeeId]);

  useEffect(() => {
    load();
    api.get("/admin/workshops").then((r) => setWorkshops(r.data.workshops || [])).catch(() => {});
  }, [load]);

  const setStatus = async (ent, status) => {
    try {
      await api.post(`/admin/entitlements/${ent.id}/status`, { status });
      toast.success(status === "active" ? "Acceso activado" : status === "revoked" ? "Acceso revocado" : "Acceso actualizado");
      load();
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const grantCourtesy = async () => {
    if (reason.trim().length < 5) {
      toast.error("Describe el motivo de la cortesía (mínimo 5 caracteres).");
      return;
    }
    if (cType === "workshop" && !cWorkshop) {
      toast.error("Selecciona el taller.");
      return;
    }
    if (cType === "dinner") {
      if (dinnerInfo?.forbidden) {
        toast.error("Tu rol no puede otorgar cortesías de la cena especial.");
        return;
      }
      if (dinnerInfo && dinnerInfo.available === 0) {
        toast.error(role === "admin" ? "No quedan lugares; marca la excepción para exceder el cupo." : "Cupo de cena agotado: no es posible otorgar la cortesía.");
        if (role !== "admin") return;
        if (!dinnerOverride) return;
      }
    }
    setActing(true);
    try {
      await api.post(`/admin/attendees/${attendeeId}/courtesy`, {
        access_type: cType,
        workshop_id: cType === "workshop" ? cWorkshop : null,
        reason: reason.trim(),
        override: cType === "dinner" ? dinnerOverride : false,
      });
      toast.success("Cortesía aplicada");
      setReason("");
      setDinnerOverride(false);
      load();
      onChanged?.();
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setActing(false);
    }
  };

  if (!ents) return <div className="flex items-center gap-2 py-4 text-hz-navy"><Loader2 className="h-4 w-4 animate-spin" /> Cargando accesos…</div>;

  return (
    <div className="space-y-4" data-testid="entitlements-panel">
      <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-hz-bg px-3 py-2">
        <p className="text-xs font-semibold text-hz-ink/70">Saldo a favor del asistente</p>
        <p className="text-sm font-bold text-hz-navy" data-testid="attendee-available-balance">
          {money((availableCents || 0) / 100)}
        </p>
      </div>
      <div>
        <p className="text-sm font-bold text-hz-navy">Derechos de acceso</p>
        <div className="mt-2 divide-y divide-hz-gray/30 rounded-xl border border-hz-gray/40">
          {ents.map((e) => (
            <div key={e.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2.5" data-testid={`entitlement-row-${e.id}`}>
              <div>
                <p className="text-sm font-semibold text-hz-ink">{e.label}</p>
                <p className="text-xs text-hz-ink/60">
                  {SOURCE_LABELS[e.source] || e.source}
                  {e.notes ? ` · ${e.notes}` : ""}
                  {e.granted_by_name ? ` · por ${e.granted_by_name}` : ""}
                  {e.granted_at ? ` · ${new Date(e.granted_at).toLocaleDateString("es-MX")}` : ""}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <span className={`rounded-full px-2.5 py-0.5 text-xs font-bold ${ENT_STATUS[e.status]?.cls || ""}`}>{ENT_STATUS[e.status]?.label || e.status}</span>
                {e.status !== "active" ? (
                  <Button size="sm" variant="outline" onClick={() => setStatus(e, "active")} data-testid={`entitlement-activate-${e.id}`} className="h-7 text-xs font-bold text-hz-success">Activar</Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => setStatus(e, "revoked")} data-testid={`entitlement-revoke-${e.id}`} className="h-7 text-xs font-bold text-hz-error">Revocar</Button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="rounded-xl border border-hz-cyan/30 bg-hz-cyan/5 p-4" data-testid="courtesy-form">
        <p className="text-sm font-bold text-hz-navy">Aplicar cortesía / invitado VIP</p>
        <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
          <div>
            <Label className="text-xs font-semibold">Acceso</Label>
            <Select value={cType} onValueChange={setCType}>
              <SelectTrigger data-testid="courtesy-type-select" className="mt-1 bg-white"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="general_event" data-testid="courtesy-type-general">Evento general</SelectItem>
                {(role === "admin" || role === "payment_reviewer") && (
                <SelectItem value="dinner" data-testid="courtesy-type-dinner">Cena especial</SelectItem>
                )}
                <SelectItem value="workshop" data-testid="courtesy-type-workshop">Taller</SelectItem>
              </SelectContent>
            </Select>
          </div>
          {cType === "workshop" && (
            <div>
              <Label className="text-xs font-semibold">Taller</Label>
              <Select value={cWorkshop} onValueChange={setCWorkshop}>
                <SelectTrigger data-testid="courtesy-workshop-select" className="mt-1 bg-white"><SelectValue placeholder="Selecciona taller" /></SelectTrigger>
                <SelectContent>
                  {workshops.map((w) => (
                    <SelectItem key={w.id} value={w.id} data-testid={`courtesy-workshop-${w.id}`}>{w.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
        {cType === "dinner" && (
          <div className="mt-3 rounded-lg border border-hz-gray/40 bg-white p-3 text-xs" data-testid="courtesy-dinner-info">
            {!dinnerInfo && <p className="text-hz-ink/60" data-testid="courtesy-dinner-loading">Consultando disponibilidad…</p>}
            {dinnerInfo?.forbidden && <p className="font-semibold text-hz-error" data-testid="courtesy-dinner-forbidden">Solo un administrador puede otorgar cortesías de la cena especial.</p>}
            {dinnerInfo && !dinnerInfo.forbidden && (
              <>
                <p className="font-semibold text-hz-ink" data-testid="courtesy-dinner-availability">
                  {dinnerInfo.available === null
                    ? "La cena no tiene límite de cupo configurado."
                    : dinnerInfo.available > 0
                      ? `Esta cortesía usará 1 de ${dinnerInfo.available} lugares disponibles de la Cena especial del viernes.`
                      : "No quedan lugares disponibles para la Cena especial del viernes."}
                </p>
                {dinnerInfo.available === 0 && role === "admin" && (
                  <label className="mt-2 flex items-start gap-2 font-semibold text-hz-error" data-testid="courtesy-dinner-override-label">
                    <Checkbox checked={dinnerOverride} onCheckedChange={(c) => setDinnerOverride(!!c)} data-testid="courtesy-dinner-override-checkbox" aria-label="Exceder cupo como excepción" />
                    Exceder el cupo como excepción administrativa (queda auditada con el motivo).
                  </label>
                )}
                {dinnerInfo.available === 0 && role !== "admin" && (
                  <p className="mt-2 font-semibold text-hz-error" data-testid="courtesy-dinner-soldout-note">Cupo agotado: no es posible otorgar la cortesía de cena.</p>
                )}
              </>
            )}
          </div>
        )}
        <div className="mt-3">
          <Label className="text-xs font-semibold">Motivo *</Label>
          <Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="courtesy-reason-input" className="mt-1 bg-white" placeholder="Ej. Invitado VIP del comité organizador" />
        </div>
        <Button onClick={grantCourtesy} disabled={acting} data-testid="courtesy-submit-btn" className="mt-3 bg-hz-cyan font-bold text-white hover:bg-hz-blue">
          {acting ? "Aplicando…" : "Aplicar cortesía"}
        </Button>
      </div>

      {(role === "admin" || role === "payment_reviewer") && (
        <div className="rounded-xl border border-hz-navy/30 bg-hz-navy/5 p-4" data-testid="modify-selections-panel">
          <p className="text-sm font-bold text-hz-navy">Modificar accesos (agregar / retirar)</p>
          <p className="mt-1 text-xs text-hz-ink/70">
            Al retirar un acceso con pago asignado, el monto se devuelve como saldo a favor del
            asistente. Los nuevos accesos consumen primero ese saldo; el resto queda pendiente.
          </p>
          <Button variant="outline" onClick={() => setModOpen(true)} data-testid="modify-selections-btn"
            className="mt-3 w-full font-bold text-hz-navy">
            Abrir modificación de accesos
          </Button>
        </div>
      )}

      {modOpen && (
        <ModifySelectionsDialog
          attendeeId={attendeeId}
          ents={ents}
          workshops={workshops}
          availableCents={availableCents}
          onClose={() => setModOpen(false)}
          onDone={() => { setModOpen(false); load(); onChanged?.(); }}
        />
      )}
    </div>
  );
};

const ModifySelectionsDialog = ({ attendeeId, ents, workshops, availableCents, onClose, onDone }) => {
  const [removeIds, setRemoveIds] = useState([]);
  const [addItems, setAddItems] = useState([]);
  const [newType, setNewType] = useState("workshop");
  const [newWorkshop, setNewWorkshop] = useState("");
  const [reason, setReason] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  const removable = (ents || []).filter((e) => e.status !== "revoked");
  const activeWsIds = new Set((ents || []).filter((e) => e.access_type === "workshop" && e.status !== "revoked" && !removeIds.includes(e.id)).map((e) => e.workshop_id));
  const addedWsIds = new Set(addItems.filter((i) => i.access_type === "workshop").map((i) => i.workshop_id));
  const toggleRemove = (id) => setRemoveIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  const hasActiveGeneral = (ents || []).some((e) => e.access_type === "general_event" && e.status !== "revoked" && !removeIds.includes(e.id)) || addItems.some((i) => i.access_type === "general_event");
  const hasActiveDinner = (ents || []).some((e) => e.access_type === "dinner" && e.status !== "revoked" && !removeIds.includes(e.id)) || addItems.some((i) => i.access_type === "dinner");

  const addToList = () => {
    if (newType === "workshop") {
      if (!newWorkshop) { toast.error("Selecciona el taller"); return; }
      if (activeWsIds.has(newWorkshop) || addedWsIds.has(newWorkshop)) {
        toast.error("El asistente ya tiene vigente este taller"); return;
      }
      setAddItems((prev) => [...prev, { access_type: "workshop", workshop_id: newWorkshop,
        label: `Taller: ${(workshops.find((w) => w.id === newWorkshop) || {}).name || ""}` }]);
      setNewWorkshop("");
    } else if (newType === "general_event") {
      if (hasActiveGeneral) { toast.error("Ya existe un acceso al evento general"); return; }
      setAddItems((prev) => [...prev, { access_type: "general_event", label: "Evento general" }]);
    } else if (newType === "dinner") {
      if (hasActiveDinner) { toast.error("Ya existe un acceso a la cena especial"); return; }
      setAddItems((prev) => [...prev, { access_type: "dinner", label: "Cena especial" }]);
    }
  };
  const removeFromAddList = (idx) => setAddItems((prev) => prev.filter((_, i) => i !== idx));

  const submit = async () => {
    if (reason.trim().length < 5) { toast.error("El motivo debe tener al menos 5 caracteres"); return; }
    if (removeIds.length === 0 && addItems.length === 0) { toast.error("Selecciona al menos un cambio"); return; }
    setSubmitting(true);
    try {
      const payload = {
        remove: removeIds,
        add: addItems.map((i) => ({ access_type: i.access_type, workshop_id: i.workshop_id || null })),
        reason: reason.trim(),
      };
      const r = await api.post(`/admin/attendees/${attendeeId}/modify-selections`, payload);
      setResult(r.data);
      toast.success("Modificación aplicada");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const availableWorkshops = workshops.filter((w) => !activeWsIds.has(w.id) && !addedWsIds.has(w.id));

  return (
    <Dialog open onOpenChange={(o) => !o && (result ? onDone() : onClose())}>
      <DialogContent className="max-h-[90vh] w-[calc(100vw-1rem)] overflow-y-auto bg-white sm:max-w-xl" data-testid="modify-selections-dialog">
        <DialogHeader>
          <DialogTitle className="font-bold text-hz-navy">Modificar accesos del asistente</DialogTitle>
        </DialogHeader>
        {result ? (
          <div className="space-y-3 text-sm" data-testid="modify-result">
            <p className="font-bold text-hz-success">Cambios aplicados correctamente.</p>
            <div className="rounded-lg bg-hz-bg px-3 py-2 text-xs">
              <p><span className="font-semibold">Accesos retirados:</span> {result.removed?.length || 0}</p>
              <p><span className="font-semibold">Accesos agregados:</span> {result.added?.length || 0}</p>
              <p><span className="font-semibold">Reembolso a saldo a favor:</span> {money((result.refund_cents || 0) / 100)}</p>
              <p data-testid="modify-result-available"><span className="font-semibold">Saldo a favor actual:</span> {money((result.available_cents || 0) / 100)}</p>
            </div>
            <Button onClick={onDone} className="w-full bg-hz-navy font-bold text-white" data-testid="modify-done-btn">Cerrar</Button>
          </div>
        ) : (
          <div className="space-y-4 text-sm">
            <div className="rounded-lg bg-hz-bg px-3 py-2 text-xs">
              <p className="font-semibold text-hz-ink/70">Saldo a favor actual: <span className="text-hz-navy font-bold" data-testid="modify-current-balance">{money((availableCents || 0) / 100)}</span></p>
            </div>

            <div>
              <p className="text-xs font-bold text-hz-navy">Retirar accesos vigentes</p>
              {removable.length === 0 ? (
                <p className="mt-1 text-xs text-hz-ink/60">No hay accesos para retirar.</p>
              ) : (
                <div className="mt-2 space-y-1">
                  {removable.map((e) => (
                    <label key={e.id} className={`flex items-start gap-2 rounded-lg border px-2 py-1.5 ${e.has_check_in ? "border-hz-gray/40 bg-hz-gray/10 opacity-60" : "border-hz-gray/40"}`} data-testid={`modify-remove-row-${e.id}`}>
                      <Checkbox checked={removeIds.includes(e.id)} onCheckedChange={() => toggleRemove(e.id)} disabled={e.has_check_in} data-testid={`modify-remove-check-${e.id}`} />
                      <div className="flex-1 text-xs">
                        <p className="font-semibold text-hz-ink">{e.label}</p>
                        <p className="text-hz-ink/60">
                          Estado: {e.status} · Asignado: {money((e.allocated_cents || 0) / 100)}
                          {e.has_check_in && <span className="ml-2 font-bold text-hz-warning" data-testid={`modify-remove-blocked-${e.id}`}>· Bloqueado: tiene check-in</span>}
                        </p>
                      </div>
                    </label>
                  ))}
                </div>
              )}
            </div>

            <div>
              <p className="text-xs font-bold text-hz-navy">Agregar accesos nuevos</p>
              <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-[1fr_1fr_auto]">
                <Select value={newType} onValueChange={setNewType}>
                  <SelectTrigger data-testid="modify-add-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="general_event" data-testid="modify-add-type-general">Evento general</SelectItem>
                    <SelectItem value="dinner" data-testid="modify-add-type-dinner">Cena especial</SelectItem>
                    <SelectItem value="workshop" data-testid="modify-add-type-workshop">Taller</SelectItem>
                  </SelectContent>
                </Select>
                {newType === "workshop" ? (
                  <Select value={newWorkshop} onValueChange={setNewWorkshop}>
                    <SelectTrigger data-testid="modify-add-workshop"><SelectValue placeholder="Selecciona taller" /></SelectTrigger>
                    <SelectContent>
                      {availableWorkshops.map((w) => (
                        <SelectItem key={w.id} value={w.id} data-testid={`modify-add-workshop-${w.id}`}>{w.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                ) : <div />}
                <Button variant="outline" onClick={addToList} data-testid="modify-add-btn" className="font-bold text-hz-navy">Agregar</Button>
              </div>
              {addItems.length > 0 && (
                <div className="mt-2 space-y-1" data-testid="modify-add-list">
                  {addItems.map((it, idx) => (
                    <div key={idx} className="flex items-center justify-between rounded-lg border border-hz-cyan/40 bg-hz-cyan/5 px-2 py-1.5 text-xs" data-testid={`modify-add-item-${idx}`}>
                      <span className="font-semibold text-hz-ink">+ {it.label}</span>
                      <Button variant="ghost" size="sm" onClick={() => removeFromAddList(idx)} data-testid={`modify-add-remove-${idx}`} className="h-6 text-xs text-hz-error">Quitar</Button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div>
              <Label className="text-xs font-semibold">Motivo *</Label>
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2}
                data-testid="modify-reason-input" className="mt-1" placeholder="Ej. Cambio de taller a petición del asistente" />
            </div>

            <div className="flex gap-2">
              <Button variant="outline" onClick={onClose} className="flex-1" data-testid="modify-cancel-btn">Cancelar</Button>
              <Button onClick={submit} disabled={submitting || reason.trim().length < 5 || (removeIds.length === 0 && addItems.length === 0)}
                className="flex-1 bg-hz-navy font-bold text-white hover:bg-hz-blue" data-testid="modify-submit-btn">
                {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                Aplicar cambios
              </Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
